import type { SocialLinks } from "../../shared/profileSocialLinks.js";
import type { CommunityContentBlock } from "./communityContent";
import {
  getSchoolsWithRoleFromSupabase, createSchoolInSupabase, getSchoolDetailFromSupabase, updateSchoolInSupabase, deleteSchoolInSupabase,
  getGradesFromSupabase, createGradeInSupabase, updateGradeInSupabase, deleteGradeInSupabase,
  getPlacesFromSupabase, createPlaceInSupabase, updatePlaceInSupabase, deletePlaceInSupabase,
  getClassroomsFromSupabase, deleteClassroomInSupabase,
  getSubjectsFromSupabase, createSubjectInSupabase, updateSubjectInSupabase, deleteSubjectInSupabase, bulkCreateSubjectsInSupabase,
  getTimeslotsFromSupabase, bulkCreateTimeslotsInSupabase,
  getTeachersFromSupabase, deleteTeacherInSupabase, getTeacherDetailFromSupabase, createTeacherInSupabase, updateTeacherInSupabase,
  getAssignmentsFromSupabase, createAssignmentInSupabase, updateAssignmentInSupabase, deleteAssignmentInSupabase,
  getFixedSchedulesFromSupabase, deleteScheduleInSupabase, createFixedScheduleInSupabase, updateFixedScheduleInSupabase, bulkCreateHomeroomFixedSchedulesInSupabase,
  getTimetablesFromSupabase, deleteTimetableInSupabase, activateTimetableInSupabase, deactivateTimetableInSupabase, copyTimetableInSupabase,
  getConstraintConfigFromSupabase, updateConstraintConfigInSupabase,
  getSchoolSummaryFromSupabase, getUnassignedListFromSupabase, getTimetableSchedulesFromSupabase,
  getAssignmentSummaryFromSupabase,
  swapSchedulesInSupabase, moveScheduleInSupabase, toggleScheduleFixedInSupabase, removeScheduleInSupabase, manualAssignInSupabase,
  getTeamTeachingGroupsFromSupabase, getTeamTeachingGroupDetailFromSupabase, createTeamTeachingGroupInSupabase, updateTeamTeachingGroupInSupabase, deleteTeamTeachingGroupInSupabase,
  type TimetableDeleteResult,
} from './supabaseTimetable';
import { importTimetableFromExcel, type ImportTimetableResult } from './timetableExcelImport';

function throwUnlessTimetableDeleteOk(
  result: TimetableDeleteResult | null,
  unavailableMessage: string
): void {
  if (!result) throw new Error(unavailableMessage);
  if (!result.ok) throw new Error(result.message);
}
import { getSupabaseMediaUrl } from "./supabaseCommunity";
import { fixSupabasePublicStorageUrl } from "./supabaseUrl";
import { formatYmdInSeoul } from "./koreaDate";
import type { QuizBattleGameQuestion, QuizBattleQuestionType } from "./quizBattleCore";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Workmate 전용 API 기본 URL.
 * 설정하지 않으면 '' (same-origin) → Vercel Functions (/api/workmate/*)로 라우팅.
 * 별도 값을 주지 않으면 same-origin(/api/workmate/*)을 호출한다.
 */
export const WORKMATE_API_BASE_URL: string =
  import.meta.env.VITE_WORKMATE_API_BASE_URL ?? '';

const BAD_HOST = "foreducator.com2";
const GOOD_HOST = "www.foreducator.com";

type DjangoDependencyEvent = {
  kind: "api-request" | "workmate-request" | "media-url" | "config";
  url: string;
  at: string;
  endpoint?: string;
  method?: string;
};

function emitDjangoDependency(event: DjangoDependencyEvent): void {
  // Phase-8 이후: Django 경로 관측 이벤트는 더 이상 발생시키지 않는다.
  void event;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("foreducator:django-dependency", { detail: event }));
  }
}

/** API 응답 내 잘못된 호스트(예: foreducator.com2)를 올바른 호스트로 치환해 ERR_NAME_NOT_RESOLVED 방지 */
function fixBadHostInValue(value: unknown): unknown {
  if (typeof value === "string") {
    return value.includes(BAD_HOST) ? value.replace(new RegExp(BAD_HOST.replace(/\./g, "\\."), "g"), GOOD_HOST) : value;
  }
  if (Array.isArray(value)) return value.map(fixBadHostInValue);
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = fixBadHostInValue(v);
    return out;
  }
  return value;
}

/** 백엔드 next URL이 잘못된 호스트(예: foreducator.com2)를 포함해도 path만 추출해 요청해 ERR_NAME_NOT_RESOLVED 방지 */
export function normalizePaginationNextUrl(nextUrl: string): string {
  if (!nextUrl || !nextUrl.startsWith("http")) return nextUrl;
  try {
    const u = new URL(nextUrl);
    return u.pathname + (u.search || "");
  } catch {
    return nextUrl;
  }
}

/**
 * GET 인터셉트에서 pathname/searchParams 파싱용.
 * `VITE_API_BASE_URL`이 비어 있으면(same-origin) `new URL(path, "")`가 "Invalid base URL"로 실패하므로
 * 브라우저 origin(또는 SSR/테스트용 localhost)을 베이스로 사용한다.
 */
function resolveUrlForApiParsing(endpoint: string, apiBaseURL: string): URL {
  const ep = endpoint.trim();
  if (ep.startsWith("http://") || ep.startsWith("https://")) {
    return new URL(ep);
  }
  const trimmed = apiBaseURL.trim();
  const base = /^https?:\/\//i.test(trimmed)
    ? `${trimmed.replace(/\/?$/, "/")}`
    : typeof window !== "undefined" && window.location?.origin
      ? `${window.location.origin}/`
      : "http://localhost/";
  const path = ep.startsWith("/") ? ep : `/${ep}`;
  return new URL(path, base);
}

export type MeProfileResponse = {
  user: {
    id: number;
    username: string;
    email: string;
    first_name?: string;
    last_name?: string;
    is_staff?: boolean;
    is_superuser?: boolean;
    date_joined?: string;
    last_login?: string | null;
  };
  profile: {
    social_links?: SocialLinks;
    telegram_id: string | null;
    nickname: string | null;
    school: { id: number; name: string } | null;
    avatar_url: string | null;
    bio: string | null;
    location: string | null;
    birthdate: string | null;
    gender: "male" | "female" | "other" | "not_say" | null;
    contact_info: string | null;
    activity_points: number;
    layout_type: "default" | "mini_sidebar" | string;
  };
};

/** 우리학교 시간표 API 응답 (날짜 -> 학급 -> 교시별 과목 배열, period_subjects: [교시, 과목들]) */
export type SchoolTimetableResponse = {
  requested_date?: string;
  date?: string | null;
  search_until?: string;
  school_name?: string;
  available_grades?: string[];
  timetable: Record<string, Record<string, (string | null)[]>>;
  period_subjects: [number, (string | null)[]][];
  class_cnt: number;
};

/** NEIS hisTimetable 프록시 (/api/school/class-timetable) — 교무실 블록용 */
export type NeisClassTimetableResponse = SchoolTimetableResponse & { date: string };

export type NotificationItem = {
  id: number;
  title: string;
  message: string;
  app: string;
  read: boolean;
  timestamp: string;
  url: string | null;
  sender: { id: number; username: string; nickname?: string | null } | null;
};

export type NotificationsResponse = {
  unread_count: number;
  results: NotificationItem[];
};

export type MessageAttachment = {
  path: string;
  name: string;
  mime: string;
  size: number;
  url?: string | null;
};

export type MessageItem = {
  id: number;
  sender: { id: number; username: string; nickname?: string | null; avatar_url?: string | null };
  recipient: { id: number; username: string; nickname?: string | null; avatar_url?: string | null };
  subject: string;
  content: string;
  read: boolean;
  read_at: string | null;
  created_at: string;
  is_sender: boolean; // 현재 사용자가 보낸 쪽지인지
  attachment: MessageAttachment | null;
  is_important: boolean;
};

export type MessagesResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: MessageItem[];
  unread_count: number;
};

export type MessageDetailResponse = MessageItem;

export type StudentRecordStatsResponse = {
  today_chat_count: number;
  chat_count: number;
};

export type StudentRecordGenerateResponse = {
  button_type: string;
  user_input: string;
  format: "text" | "list";
  result: string;
  items?: string[];
  byte_count?: number;
};

export type StudentRecordItem = {
  id: number;
  button_type: string;
  user_input: string;
  chat_response: string;
  created_at: string | null;
};

export type StudentRecordRecordsResponse = {
  results: StudentRecordItem[];
  count: number;
  next: string | null;
  previous: string | null;
};

export type EvaluationPlanItem = {
  "번호": string;
  "수업방법": string;
  "평가 방법 및 수업-평가 연계의 주안점": string;
};

export type EvaluationPlanGenerateResponse = {
  user_input: string;
  items: EvaluationPlanItem[];
  raw?: string;
  cached?: boolean;
};

export type ExamScoreRow = {
  difficulty: string;
  score: number;
  questions: number;
  question_weight: string;
  score_weight: string;
};

export type ExamScoreCalculateResponse = {
  result: ExamScoreRow[];
};

export type CustomGptCategory = "EDU" | "WORK" | "LIFE" | "HOBBY" | "TECH" | "OTHER";

export type CustomGptItem = {
  id: number;
  author: { id: number; username: string; nickname?: string | null };
  title: string;
  description: string;
  category: CustomGptCategory;
  is_public: boolean;
  use_count: number;
  like_count: number;
  dislike_count: number;
  score: number;
  created_at: string;
  updated_at: string;
  is_owner: boolean;
  is_liked: boolean;
  is_disliked: boolean;
  system_prompt?: string;
  example_prompts?: string;
  example_prompts_list?: string[];
};

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export type CustomGptListResponse = PaginatedResponse<CustomGptItem>;
export type CustomGptChatResponse = { gpt_id: number; messages: Array<{ role: "user" | "assistant" | string; content: string; created_at?: string }> };
export type CustomGptSendResponse = { response: string; messages: Array<{ role: "user" | "assistant" | string; content: string; created_at?: string }> };

// Workmate: 영수증 자동정리(Receipts)
export type ReceiptsTopic = {
  id: number;
  name: string;
  created_at: string;
  total_budget: number;
  remaining_budget: number;
  access_role?: 'owner' | 'editor' | 'viewer';
  is_owner?: boolean;
  share_permission?: 'viewer' | 'editor' | null;
};

export type ReceiptsTopicsResponse = { results: ReceiptsTopic[] };

export type ReceiptsTransaction = {
  type: "income" | "expense";
  id: number;
  date: string | null;
  amount: number | null;
  vendor?: string | null;
  description?: string | null;
  processing_status?: string | null;
};

export type ReceiptsTopicTransactionsResponse = {
  topic: ReceiptsTopic;
  transactions: ReceiptsTransaction[];
};

export type ReceiptRecord = {
  id: number;
  topic: number;
  image_url: string | null;
  date: string | null;
  vendor: string | null;
  description: any;
  amount: number | null;
  address: string | null;
  business_number: string | null;
  representative: string | null;
  phone_number: string | null;
  card_type: string | null;
  card_number: string | null;
  installment_months: number | null;
  created_at: string;
};

export type IncomeRecord = {
  id: number;
  topic: number;
  amount: number;
  description: string;
  date: string;
  created_at: string;
};

// Workmate: 논술형 평가 채점
export type EssayAssessment = {
  id: number;
  title: string;
  problem: string;
  grading_criteria: string;
  created_at: string;
  updated_at: string;
};

export type EssayGradingRecordFile = {
  id: number;
  file: string;
  file_url: string | null;
  created_at: string;
};

export type EssayGradingRecord = {
  id: number;
  assessment: number;
  student_name: string | null;
  student_number: string | null;
  raw_ocr_text: string | null;
  score: string | null;
  rationale: string | null;
  student_comment: string | null;
  processing_status: string;
  processing_error: string | null;
  processed_at: string | null;
  created_at: string;
  files: EssayGradingRecordFile[];
};

// Classmate: 논술형 평가 (학급 연동, 루브릭)
export type ClassmateEssayCriterion = {
  id: string;
  assessment_id?: string;
  order_index: number;
  name: string;
  description: string;
  max_points: number;
  levels?: unknown[];
  created_at?: string;
};

export type ClassmateEssayAssessment = {
  id: string;
  title: string;
  problem: string;
  instructions: string;
  model_answer: string;
  grading_method: string;
  problem_image_paths: string[];
  problem_image_urls: (string | null)[];
  grading_method_image_paths: string[];
  grading_method_image_urls: (string | null)[];
  total_points: number;
  classroom_id: number | null;
  classroom_name: string | null;
  criteria: ClassmateEssayCriterion[];
  created_at: string;
  updated_at: string;
};

export type ClassmateEssaySubmissionFile = {
  id: string;
  storage_path: string;
  original_name: string;
  mime: string;
  file_url: string | null;
  created_at: string;
};

export type ClassmateEssayCriterionScore = {
  id: string;
  criterion_id: string;
  criterion_name: string | null;
  max_points: number | null;
  score: number;
  rationale: string;
  created_at?: string;
};

export type ClassmateEssayScoreItem = {
  id?: string;
  name: string;
  max_points: number;
  score: number;
  rationale: string;
};

export type ClassmateEssayPageRange = {
  start_page?: number;
  end_page?: number;
  batch?: boolean;
  batch_id?: string;
  page_count?: number;
  awaiting_review?: boolean;
  proposed_students?: {
    student_name: string | null;
    student_number: string | null;
    start_page: number;
    end_page: number;
  }[];
  notes?: string;
  skipped_pages?: number[];
  from_batch?: boolean;
};

export type ClassmateEssaySubmission = {
  id: string;
  assessment_id: string;
  student_id: number | null;
  student_name: string | null;
  student_number: string | null;
  match_status: "matched" | "unmatched" | "ambiguous";
  raw_ocr_text: string | null;
  score_breakdown: ClassmateEssayScoreItem[];
  page_range: ClassmateEssayPageRange | null;
  total_score: number | null;
  overall_feedback: string;
  status: "pending" | "processing" | "completed" | "failed";
  error_message: string;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  regrade_count: number;
  is_manual_override: boolean;
  files: ClassmateEssaySubmissionFile[];
  criterion_scores: ClassmateEssayCriterionScore[];
};

export type ClassmateEssayReportStats = {
  count: number;
  total_submissions: number;
  average: number | null;
  max: number | null;
  min: number | null;
  stddev: number | null;
  distribution: { label: string; count: number }[];
  per_criterion: {
    criterion_id: string;
    name: string;
    max_points: number;
    average: number | null;
    count: number;
  }[];
};

export type ClassmateEssayReport = {
  assessment: ClassmateEssayAssessment;
  stats: ClassmateEssayReportStats;
  table: {
    submission_id: string;
    student_id: number | null;
    student_number: string | null;
    student_name: string | null;
    match_status: string;
    total_score: number | null;
    criterion_scores: ClassmateEssayCriterionScore[];
    overall_feedback: string;
  }[];
  submissions: ClassmateEssaySubmission[];
};

// Informate: 교사들의 책(Books)
export type InformateBookItem = {
  submitted_by_id?: number;
  community_post_id?: number | null;
  id: number;
  title: string;
  author: string;
  publisher: string;
  publication_date: string | null;
  isbn: string;
  image_url: string | null;
  price?: string | null;
  store_url?: string | null;
  description: string;
  recommendation_count: number;
  submission_date: string;
  approved_date: string | null;
  is_new: boolean;
  is_recently_added: boolean;
  is_recommended: boolean;
};

export type InformateBooksListResponse = PaginatedResponse<InformateBookItem>;

export type InformateAdminBookItem = InformateBookItem & {
  status: "pending" | "approved" | "rejected" | string;
  submitted_by_username: string | null;
};

export type InformateAdminBooksListResponse = PaginatedResponse<InformateAdminBookItem>;

// Informate: 교사들의 유튜브(Youtuber)
export type YoutuberSchoolType = "E" | "M" | "H" | "S" | "K";
export type YoutuberCategory = "latest" | "popular" | "liked";

export type YoutuberPostItem = {
  id: number;
  youtuber: number;
  youtuber_name: string;
  youtuber_school_type_display: string;
  date: string;
  title: string;
  link: string;
  thumbnail_url: string;
  content: string | null;
  created_string: string;
};

export interface YoutuberItem {
  id: number;
  user: number;
  youtube_id: string;
  latest_post_date: string | null;
  name: string | null;
  affiliation: string | null;
  introduction: string | null;
  thumbnail_image_url: string | null;
  school_type: YoutuberSchoolType;
  school_type_display: string;
  subject: string | null;
  last_updated: string;
  likes_count: number;
  is_liked: boolean;
  posts: YoutuberPostItem[];
  recommendation_count: number;
  is_recommended: boolean;
};

export type YoutubersListResponse = PaginatedResponse<YoutuberItem>;
export type YoutuberPostsListResponse = PaginatedResponse<YoutuberPostItem>;

// Informate: 교사들의 블로그(Bloger) - 블로그 정보만, post 미포함
export type BlogerSchoolType = "E" | "M" | "H" | "S" | "K";
export type BlogerCategory = "popular" | "latest" | "liked";

export type BlogerItem = {
  id: number;
  user: number;
  naver_id: string;
  latest_post_date: string | null;
  name: string | null;
  affiliation: string | null;
  introduction: string | null;
  thumbnail_image_url: string | null;
  school_type: BlogerSchoolType;
  school_type_display: string;
  subject: string | null;
  last_updated: string;
  likes_count: number;
  is_liked: boolean;
};

export type BlogersListResponse = PaginatedResponse<BlogerItem>;

// Informate: 교사들의 바이브코딩
export type InformateVibeSiteSort = "latest" | "popular" | "hits";
export type InformateVibeSiteItem = import("./supabaseInformateVibeSites").InformateVibeSiteItem;
export type InformateVibeSiteComment = import("./supabaseInformateVibeSites").InformateVibeSiteComment;
export type InformateVibeSitesListResponse = PaginatedResponse<InformateVibeSiteItem>;

// Informate: 교사 오픈채팅(OpenChats)
export type OpenChatCategory = { id: number; name: string };
export type OpenChatItem = {
  id: number;
  name: string;
  link: string;
  description: string;
  category: OpenChatCategory;
  tags: string[];
  created_by_username: string;
  created_at: string | null;
  can_edit: boolean;
};
export type OpenChatsListResponse = PaginatedResponse<OpenChatItem>;
export type OpenChatInfo = {
  id: number;
  name: string;
  link: string;
  description: string;
  category: number;
  tags: string;
  password: string;
};
export type OpenChatPasswordItem = {
  id: number;
  password: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
  score: number;
  upvotes: number;
  downvotes: number;
  user_voted_up: boolean;
  user_voted_down: boolean;
};

// Classmate: 학급/학생/클래스 운영 (v1)
export type ClassmateClassroom = {
  id: number;
  name: string;
  student_count?: number;
  /** 학급 단위 교사 메모 (활동 기록 요약 등) */
  memo?: string | null;
  /** category_id가 null인 활동 기록에 표시할 기본 평가 항목 이름 */
  activity_default_category_name?: string | null;
  activity_default_category_sort_order?: number;
  activity_default_category_icon?: string | null;
};

export type ClassmateStudent = {
  id: number;
  classroom: number;
  number: number;
  name: string;
  gender: "M" | "F";
  memo: string | null;
  total_score?: number; // derived
  /** Supabase 전용: classmate-student-photos 버킷 내 경로 */
  photo_storage_path?: string | null;
};

export type ClassmateClassroomsResponse = { results: ClassmateClassroom[] };
export type ClassmateStudentsResponse = { results: ClassmateStudent[] };

export type ClassmateActivityCategory = {
  id: number;
  classroom: number;
  name: string;
  sort_order: number;
  icon: string;
};

export type ClassmateActivityRecord = {
  id: number;
  student: number;
  recorded_at: string;
  content: string;
  score_change: number;
  category: number | null;
  category_name?: string | null;
};

export type ClassmateActivityBulkEntry = {
  content: string;
  score_change?: number;
  category?: number | null;
};

export type ClassmateActivitiesResponse = { results: ClassmateActivityRecord[] };

export type ClassmateRandomPickResponse = {
  success: boolean;
  students: Array<{ id: number; number: number; name: string; gender: "M" | "F" }>;
};

import type { ArrangementRandomizeStrategy } from "./classmateSociometrySeating";

export type { ArrangementRandomizeStrategy } from "./classmateSociometrySeating";

export type ClassmateSeatArrangement = {
  id: number;
  classroom: number;
  title: string;
  arrangement_type: "RC" | "GR";
  created_at: string;
  total_rows: number | null;
  total_cols: number | null;
  group_size: number | null;
  num_groups: number | null;
  gender_balance: boolean;
  excluded_pairs: string;
  is_favorite?: boolean;
  /** DB 기본값 legacy_random — 마음관계 전략 사용 시 변경 */
  randomize_strategy?: ArrangementRandomizeStrategy | string | null;
  /** null이면 해당 학급 최신 응답 설문 자동 선택 */
  sociometry_survey_id?: number | null;
  /** 미리 지정(고정석): seatId(string) → studentId. 배치 설정 전용 */
  pinned_assignments?: Record<string, number> | null;
};

export type ClassmateSeat = {
  id: number;
  arrangement: number;
  student: ClassmateStudent | null;
  row: number | null;
  col: number | null;
  group_number: number | null;
  order_in_group: number | null;
};

export type ClassmateArrangementDetail = ClassmateSeatArrangement & { seats: ClassmateSeat[] };

export type ClassmateVote = {
  id: number;
  classroom: number;
  title: string;
  created_at: string;
  is_active: boolean;
  cooldown_seconds: number;
  total_votes: number;
  candidates: Array<{ id: number; number: number; name: string }>;
};

export type ClassmateVoteResult = {
  vote: number;
  results: Array<{ candidate: { id: number; number: number; name: string }; vote_count: number }>;
};

export type ClassmateProgress = {
  id: number;
  subject: string;
  start_date: string;
  end_date: string;
  created_at: string;
  is_favorite: boolean;
  classrooms: number[];
};

export type ClassmateProgressEntry = {
  id: number;
  class_progress: number;
  classroom: number | null;
  date: string;
  period: number;
  subject: string | null;
  class_progress_subject?: string;
  classroom_name?: string;
  pages_completed: number;
  notes: string | null;
  display_color: string | null;
  lesson_count?: number;
};

export type ClassmateProgressDayMemo = {
  id: number;
  class_progress_id: number;
  date: string;
  memo: string;
  updated_at: string;
};

export type ClassmateProgressDetail = Omit<ClassmateProgress, "classrooms"> & { classrooms: Array<{ id: number; name: string }> };
export type ClassmateTodayEntry = ClassmateProgressEntry;

// Classmate: Card flip game (gameapp)
export type ClassmateGame = {
  id: number;
  title: string;
  subject: string;
  description: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  card_count?: number;
  is_owner?: boolean;
};

export type ClassmateGameCard = {
  id: number;
  question_text: string;
  answer_text: string;
  question_image_url: string | null;
  answer_image_url: string | null;
};

export type ClassmateGameDetail = ClassmateGame & { cards: ClassmateGameCard[] };

export type ClassmateGameStartResponse = {
  success: boolean;
  game: { id: number; title: string; subject: string; description: string; is_public: boolean };
  config: { players: number; pair_count: number; include_bonus: boolean };
  deck: Array<{ uid: string; pair_key: string; face_type: "question" | "answer" | "bonus"; text: string; image_url: string | null }>;
};

// Classmate: 퀴즈 배틀
export type QuizBattleSetItem = {
  id: number;
  title: string;
  subject: string;
  description: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  question_count?: number;
  favorite_count?: number;
  is_owner?: boolean;
};

function normalizeQuizBattleFavoriteIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<number>();
  const out: number[] = [];
  for (const v of raw) {
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n)) continue;
    const id = Math.floor(n);
    if (id <= 0 || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export type QuizBattleChoiceItem = {
  id: string;
  text: string;
  image_path?: string | null;
  order: number;
};

export type QuizBattleQuestionItem = {
  id: number;
  question_text: string;
  answer_text: string;
  order: number;
  question_type?: QuizBattleQuestionType;
  question_image_path?: string | null;
  choices_json?: unknown;
};

export type QuizBattleQuestionPayload = {
  question_text: string;
  answer_text: string;
  question_type?: QuizBattleQuestionType;
  question_image_path?: string | null;
  choices?: QuizBattleChoiceItem[];
};

export type QuizBattleSetDetail = QuizBattleSetItem & { questions: QuizBattleQuestionItem[] };

export type QuizBattleStartResponse = {
  success: boolean;
  game: { id: number; title: string; subject: string; description: string; is_public: boolean };
  questions: QuizBattleGameQuestion[];
};

// Community: 소통공간 게시판(TBoard)
export type CommunitySubCategory = { id: number; name: string; category_id: number };
export type CommunityCategory = { id: number; name: string; subcategories: CommunitySubCategory[] };

export type CommunityPostAuthor = {
  social_links?: SocialLinks;
  id: number | null;
  username: string;
  nickname: string | null;
  avatar_url: string | null;
};

export type CommunityBlockedUser = {
  id: number;
  username: string;
  nickname: string | null;
  avatar_url: string | null;
  blocked_at: string;
};

/** 게시글 작성자 프로필 카드 API 응답 */
export type CommunityUserProfile = {
  nickname: string;
  bio: string;
  avatar_url: string | null;
  followers_count: number;
  following_count: number;
  activity_points: number;
  memo: { content: string; color: string | null } | null;
  is_following: boolean;
  username: string;
};

export type CommunityPostFile = { id: number; filename: string; url: string | null; uploaded_at: string };

export type CommunityPostListItem = {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  hits: number;
  top_fixed: boolean;
  tags: string | null;
  category: string | null;
  category_new: { id: number; name: string | null } | null;
  subcategory: { id: number; name: string | null; category_id: number | null } | null;
  author: CommunityPostAuthor;
  head_image_url: string | null;
  file_upload_url: string | null;
  file_upload_name: string | null;
  num_comments: number;
  num_likes: number;
  is_liked: boolean;
  is_bookmarked: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_pin: boolean;
  /** 바이브코딩 카테고리 글일 때 결과 페이지 URL (상세에서 iframe 미리보기 시도 + 새창 폴백) */
  vibe_coding_link: string | null;
};

export type CommunityPostDetail = CommunityPostListItem & {
  content: string;
  additional_files: CommunityPostFile[];
  topic_id: number | null;
  grade: number | null;
};

export type CommunityComment = {
  id: number;
  post_id: number;
  parent_id: number | null;
  content: string;
  is_deleted: boolean;
  created_at: string;
  modified_at: string | null;
  created_string: string | null;
  author: CommunityPostAuthor;
  comment_image_url: string | null;
  comment_file_url: string | null;
  comment_file_name: string | null;
  replies: CommunityComment[];
  can_edit: boolean;
  can_delete: boolean;
  num_likes: number;
  is_liked: boolean;
};

export type CommunityHighlightsResponse = {
  latest: CommunityPostListItem[];
  popular: CommunityPostListItem[];
  following: CommunityPostListItem[];
};

export type ClassmateAttendanceRow = {
  id: number;
  student: number;
  status: "present" | "late" | "early_leave" | "absent_lesson" | "absent" | "excused";
  reason: "sickness" | "unexcused" | "other" | "excused" | null;
  note: string | null;
  recorded_at: string;
  updated_at: string;
};

export type ClassmateAttendanceRegister = {
  id: number;
  classroom_id: number;
  name: string;
  start_date: string;
  end_date: string;
  created_at: string;
  latest_recorded_date?: string | null;
  student_count?: number;
};

export type ClassmateDailyAttendanceMergedRow = {
  student_id: number;
  status: ClassmateAttendanceRow["status"] | null;
  reason: ClassmateAttendanceRow["reason"];
  note: string | null;
  source: "manual" | "derived" | "none";
  derived_status: ClassmateAttendanceRow["status"] | null;
  derived_reason: ClassmateAttendanceRow["reason"];
  manual_id: number | null;
  period_records: Array<{
    status: ClassmateAttendanceRow["status"];
    reason?: ClassmateAttendanceRow["reason"];
    note?: string | null;
    period?: number | null;
    subject?: string | null;
    entry_id?: number | null;
    date?: string | null;
  }>;
};

// ── Timetable Manager Types ─────────────────────────────────────────────

export type TTSchoolRole = 'owner' | 'admin' | 'viewer' | 'teacher';

export type TTSchool = {
  id: number;
  name: string;
  total_grades: number;
  created_at: string;
  updated_at: string;
  teacher_count: number;
  classroom_count: number;
  timetable_count: number;
  has_active_timetable: boolean;
  my_role?: TTSchoolRole;
};

export type TTSchoolMember = {
  id: number;
  user_id: number;
  username: string | null;
  email: string | null;
  nickname: string | null;
  role: 'admin' | 'viewer';
  created_at: string;
};

export type TTSwapLessonSnapshot = {
  date: string; period: number; teacher_id: number; teacher_user_id: number | null;
  teacher_name: string; subject_id: number; subject_name: string;
  classroom_id: number; classroom_name: string; place_id: number | null; status: string;
};

export type TTChainRequestMove = {
  from_id: number;
  to_id: number;
  snapshot: TTSwapLessonSnapshot;
  destination: TTSwapLessonSnapshot;
};
export type TTChainParticipant = {
  teacher_id: number;
  user_id: number | null;
  name: string;
  approved_at: string | null;
};

export type TTSwapRequest = {
  chain_moves?: TTChainRequestMove[] | null;
  chain_participants?: TTChainParticipant[] | null;
  participant_user_ids?: number[];
  id: number;
  school_id: number;
  timetable_id: number;
  requester_user_id: number;
  counterpart_user_id: number | null;
  source_weekly_id: number;
  target_weekly_id: number;
  source_teacher_id: number;
  target_teacher_id: number;
  status: 'PENDING_COUNTERPART' | 'PENDING_ADMIN' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'REVERTED';
  reverted_at?: string | null;
  reverted_by?: number | null;
  revert_reason?: string | null;
  message: string | null;
  reject_reason: string | null;
  counterpart_acted_at: string | null;
  admin_approved_at: string | null;
  created_at: string;
  updated_at: string;
  source_user_id: number | null;
  source_approved_at: string | null;
  target_approved_at: string | null;
  source_snapshot: TTSwapLessonSnapshot | null;
  target_snapshot: TTSwapLessonSnapshot | null;
  admin_approved_without_consent?: boolean;
  neis_only?: boolean;
  operation_id?: string | null;
  kind?: "SWAP" | "COVER";
  // joined
  requester_name?: string;
  counterpart_name?: string;
  source_date?: string;
  source_period?: number;
  source_teacher_name?: string;
  target_date?: string;
  target_period?: number;
  target_teacher_name?: string;
};

export type TTUserSearchResult = {
  id: number;
  username: string | null;
  email: string | null;
  nickname: string | null;
};

export type TTGradeInfo = {
  id: number;
  grade: number;
  num_classes: number;
  periods_monday: number;
  periods_tuesday: number;
  periods_wednesday: number;
  periods_thursday: number;
  periods_friday: number;
  total_periods: number;
};

export type TTPlace = {
  id: number;
  name: string;
  is_shared: boolean;
  max_concurrent_classes: number;
  description: string;
};

export type TTClassroom = {
  id: number;
  grade: number;
  class_num: number;
  default_place: number | null;
  default_place_name: string | null;
  homeroom_teacher_name: string | null;
};

export type TTSubject = {
  id: number;
  name: string;
  default_place: number | null;
  default_place_name: string | null;
  teacher_count: number;
};

export type TTTeacher = {
  id: number;
  name: string;
  homeroom: number | null;
  homeroom_display: string | null;
  subject_names: string[];
  subject_ids?: number[];
  unavailable_count: number;
  has_user: boolean;
  user_id: number | null;
  linked_username?: string | null;
  linked_nickname?: string | null;
};

export type TTTeacherDetail = TTTeacher & {
  subjects: number[];
  unavailable_times: { id: number; day: string; period: number }[];
};

export type TTTeachingAssignment = {
  id: number;
  teacher: number;
  teacher_name: string;
  classroom: number;
  classroom_display: string;
  subject: number;
  subject_name: string;
  hours_per_week: number;
  is_block: boolean;
  block_size: number;
  single_hours: number;
  place: number | null;
  place_name: string | null;
};

export type TTSchedule = {
  id: number;
  teacher: number;
  teacher_name: string;
  classroom: number | null;
  classroom_display: string | null;
  subject: number;
  subject_name: string;
  day: string;
  period: number;
  place: number | null;
  place_name: string | null;
  schedule_type: string;
  schedule_type_display: string;
  is_fixed: boolean;
  description: string | null;
  target_grade: number | null;
  max_students: number | null;
};

export type TTTeamTeachingGroup = {
  id: number;
  school: number;
  subject: number;
  subject_name: string;
  hours_per_week: number;
  teacher_ids: number[];
  teacher_names: string[];
  classroom_ids: number[];
  classroom_displays: string[];
  is_fixed: boolean;
  day: string | null;
  period: number | null;
};

export type TTGenerationConflictItem = {
  type: string;
  day?: string;
  period?: number;
  message: string;
};

export type TTGenerationUnassignedItem = {
  teacher: string;
  classroom: string;
  subject: string;
  hour_index?: number;
  reason_code?: "NO_CLASSROOM_SLOT" | "TEACHER_UNAVAILABLE" | "TEACHER_CONFLICT" | string;
  reason_message?: string;
};

/** 생성 시점 제약 스냅샷 (DB에 generation_report로 저장) */
export type TTConstraintsAtGeneration = {
  teacher_max_daily_periods: number;
  max_consecutive_periods: number;
  equalize_daily_hours?: boolean;
  equalize_undesirable_periods?: boolean;
  undesirable_periods?: unknown;
  distribute_same_subject?: boolean;
  generator_note?: string;
};

/** 시간표 자동 생성 직후 DB에 저장되는 요약 (schedule_manager_timetable.generation_report) */
export type TTGenerationReport = {
  generated_at: string;
  success: boolean;
  schedule_count: number;
  conflict_count: number;
  conflicts: TTGenerationConflictItem[];
  conflicts_omitted_count?: number;
  partial: boolean;
  errors: string[];
  suggestions: string[];
  unassigned: TTGenerationUnassignedItem[];
  unassigned_total?: number;
  unassigned_omitted_count?: number;
  forced_assignments?: TTGenerationUnassignedItem[];
  /** 미배정 사유 코드별 건수 */
  unassigned_by_reason?: Record<string, number>;
  /** 생성 시점 constraintconfig 스냅샷 */
  constraints_at_generation?: TTConstraintsAtGeneration | null;
  algorithm_meta?: {
    generator?: string;
    multi_attempts_max?: number;
    winning_attempt_index?: number;
  };
};

export type TTTimetable = {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  schedule_count: number;
  created_at: string;
  updated_at: string;
  generation_report?: TTGenerationReport | null;
};

export type TTTimetableDetail = TTTimetable & {
  schedules: TTSchedule[];
};

export type TTConstraintConfig = {
  id: number;
  max_consecutive_periods: number;
  teacher_max_daily_periods: number;
  equalize_daily_hours: boolean;
  equalize_undesirable_periods: boolean;
  undesirable_periods: number[];
  distribute_same_subject: boolean;
  custom_rules: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type TTSetupProgress = {
  grades: boolean;
  classrooms: boolean;
  teachers: boolean;
  subjects: boolean;
  places: boolean;
  timeslots: boolean;
  assignments: boolean;
  constraints: boolean;
  all_assigned: boolean;
  unassigned_classrooms: string[];
  ready_to_generate: boolean;
};

export type TTSchoolSummary = {
  school: TTSchool;
  grades: TTGradeInfo[];
  classrooms: TTClassroom[];
  teachers: TTTeacher[];
  subjects: TTSubject[];
  places: TTPlace[];
  assignments: TTTeachingAssignment[];
  timetables: TTTimetable[];
  constraints: TTConstraintConfig | null;
  setup_progress: TTSetupProgress;
};

export type TTUnassignedItem = {
  assignment_id: number;
  teacher_id: number;
  teacher_name: string;
  classroom_id: number;
  classroom_display: string;
  subject_id: number;
  subject_name: string;
  place_id: number | null;
  unassigned: number;
  hours_per_week?: number;
  is_block?: boolean;
  block_size?: number;
  single_hours?: number;
};

export type TTSubstitutionHistory = {
  timestamp: string;
  type: 'SWAP' | 'UPDATE' | 'RESTORE' | 'COVER';
  operation_id?: string;
  actor_id?: number;
  actor_name?: string;
  reason?: string;
  date?: string;
  period?: number;
  classroom_id?: number;
  from_teacher_id: number;
  from_teacher_name: string;
  from_subject_id: number;
  from_subject_name: string;
  to_teacher_id: number;
  to_teacher_name: string;
  to_subject_id: number;
  to_subject_name: string;
  target_info?: string;
};

export type TTNeisAssignment = {
  teacher_id: number; teacher_name: string; subject_id: number; subject_name: string; place_id: number | null;
};
export type TTNeisHistory = {
  timestamp: string; request_id: number; message: string | null; neis_only: boolean;
  source_date: string; source_period: number; target_date: string; target_period: number;
  from_teacher_name: string; from_subject_name: string; to_teacher_name: string; to_subject_name: string;
};
export type TTWeeklySchedule = {
  id: number;
  school_id: number;
  timetable_id: number;
  original_schedule_id: number | null;
  date: string;
  period: number;
  teacher_id: number;
  teacher_name?: string;
  classroom_id: number;
  classroom_display?: string;
  subject_id: number;
  subject_name?: string;
  place_id: number | null;
  status: 'SCHEDULED' | 'CANCELLED';
  is_modified?: boolean;
  memo?: string | null;
  substitution_history?: TTSubstitutionHistory[];
  /** Individual management-view color; takes precedence over teacher/subject color. */
  cell_color?: string | null;
  neis_override?: TTNeisAssignment | null;
  neis_history?: TTNeisHistory[];
};

export type TTWeeklyPeriodCounts = {
  total_scheduled: number;
  total_cancelled: number;
  by_subject: { subject_name: string; count: number }[];
  by_teacher: { teacher_name: string; count: number }[];
  by_classroom: { classroom_display: string; count: number }[];
};

export type TTAssignmentSummaryItem = {
  classroom_id: number;
  display: string;
  grade: number;
  class_num: number;
  total_periods: number;
  assigned_hours: number;
  fixed_hours: number;
  total: number;
  remaining: number;
};

export type TTGenerateStatus = {
  task_id: string;
  status: string;
  progress?: { step: string; percent: number; message: string };
  result?: {
    success: boolean;
    timetable_id: number | null;
    timetable_name?: string;
    schedule_count?: number;
    conflict_count?: number;
    conflicts?: TTGenerationConflictItem[];
    partial?: boolean;
    unassigned?: TTGenerationUnassignedItem[];
    errors?: string[];
    suggestions?: string[];
    generated_at?: string;
    unassigned_total?: number;
    unassigned_omitted_count?: number;
    unassigned_by_reason?: Record<string, number>;
    constraints_at_generation?: TTConstraintsAtGeneration | null;
    algorithm_meta?: TTGenerationReport["algorithm_meta"];
  };
  error?: string;
};

export type TTGenerateStartRequest = {
  name?: string;
};

export type TTGenerateStartResponse = {
  task_id: string;
  status: "PENDING" | "SUCCESS" | "FAILURE" | string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseGenerateStartResponse(raw: unknown): TTGenerateStartResponse {
  if (!isRecord(raw)) {
    throw new Error("Invalid timetable generate response: expected object");
  }
  const taskId = raw.task_id;
  const status = raw.status;
  if (typeof taskId !== "string" || !taskId) {
    throw new Error("Invalid timetable generate response: task_id is required");
  }
  if (typeof status !== "string" || !status) {
    throw new Error("Invalid timetable generate response: status is required");
  }
  return { task_id: taskId, status };
}

function parseGenerateStatusResponse(raw: unknown): TTGenerateStatus {
  if (!isRecord(raw)) {
    throw new Error("Invalid timetable generate-status response: expected object");
  }
  const taskId = raw.task_id;
  const status = raw.status;
  if (typeof taskId !== "string" || !taskId) {
    throw new Error("Invalid timetable generate-status response: task_id is required");
  }
  if (typeof status !== "string" || !status) {
    throw new Error("Invalid timetable generate-status response: status is required");
  }
  return raw as TTGenerateStatus;
}

export type TTConflict = {
  type: string;
  message: string;
  schedule_ids: number[];
};

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Workmate 전용 fetch – WORKMATE_API_BASE_URL(기본값: '')을 사용해
   * Vercel Functions(/api/workmate/*)를 same-origin으로 직접 호출.
   * Bearer는 Supabase 세션 JWT만 사용(Django access_token과 혼용하지 않음).
   * 401 시 Django refresh 대신 Supabase refreshSession 후 1회만 재시도.
   */
  async workmateRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetryAfterSupabaseRefresh = false
  ): Promise<T> {
    const { supabase: sb } = await import('./supabase');
    const supabaseToken = sb ? (await sb.auth.getSession()).data.session?.access_token : null;

    const isFormData =
      typeof FormData !== "undefined" &&
      (options.body instanceof FormData || Object.prototype.toString.call(options.body) === "[object FormData]");
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };
    if (isFormData) {
      delete headers["Content-Type"];
      delete headers["content-type"];
    } else {
      headers["Content-Type"] = headers["Content-Type"] || headers["content-type"] || "application/json";
    }
    if (supabaseToken) headers['Authorization'] = `Bearer ${supabaseToken}`;

    const url = `${WORKMATE_API_BASE_URL}${endpoint}`;
    emitDjangoDependency({
      kind: "workmate-request",
      url,
      endpoint,
      method: (options.method || "GET").toUpperCase(),
      at: new Date().toISOString(),
    });
    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      if (response.status === 401) {
        if (!isRetryAfterSupabaseRefresh && sb) {
          const { data } = await sb.auth.refreshSession();
          if (data.session?.access_token) {
            return this.workmateRequest<T>(endpoint, options, true);
          }
        }
        throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
      }
      if (response.status === 429) {
        throw new Error('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
      }
      const errorBody = await response.text().catch(() => '{}');
      let msg = '요청에 실패했습니다.';
      try { const j = JSON.parse(errorBody); msg = j.error || j.message || j.detail || msg; } catch { /* ignore */ }
      const err = new Error(msg);
      (err as any).status = response.status;
      throw err;
    }

    const text = await response.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new Error('Invalid JSON response from server');
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Intercept GET community posts list (load more) so Supabase is used when possible
    const method = (options.method || "GET").toUpperCase();
    if (method === "GET") {
      const url = resolveUrlForApiParsing(endpoint, this.baseURL);
      const pathname = url.pathname.replace(/\/$/, "") || "/";
      if (pathname.endsWith("/api/community/posts") && url.searchParams.has("page")) {
        try {
          const { listCommunityPostsFromSupabase } = await import("./supabaseCommunity");
          const page = Number(url.searchParams.get("page")) || 1;
          const limit = Number(url.searchParams.get("limit")) || 20;
          const cat = url.searchParams.get("category_id");
          const sub = url.searchParams.get("subcategory_id");
          const data = await listCommunityPostsFromSupabase({
            page,
            limit,
            category_id: cat ? (Number(cat) || ("" as const)) : ("" as const),
            subcategory_id: sub ? (Number(sub) || ("" as const)) : ("" as const),
            sort: (url.searchParams.get("sort") as "recent" | "popular" | "hits" | "likes") || "recent",
            q: url.searchParams.get("q") ?? "",
            tag: url.searchParams.get("tag") ?? "",
            author: url.searchParams.get("author") ? Number(url.searchParams.get("author")) : undefined,
          });
          if (data) {
            const hasMore = page * limit < data.count;
            return {
              count: data.count,
              results: data.results,
              next: hasMore
                ? `${this.baseURL}/api/community/posts/?page=${page + 1}&limit=${limit}&q=${encodeURIComponent(url.searchParams.get("q") || "")}&sort=${url.searchParams.get("sort") || "recent"}&tag=${encodeURIComponent(url.searchParams.get("tag") || "")}${url.searchParams.get("category_id") ? `&category_id=${url.searchParams.get("category_id")}` : ""}${url.searchParams.get("subcategory_id") ? `&subcategory_id=${url.searchParams.get("subcategory_id")}` : ""}${url.searchParams.get("author") ? `&author=${url.searchParams.get("author")}` : ""}`
                : null,
              previous: page > 1 ? `${this.baseURL}/api/community/posts/?page=${page - 1}&limit=${limit}&q=${encodeURIComponent(url.searchParams.get("q") || "")}&sort=${url.searchParams.get("sort") || "recent"}&tag=${encodeURIComponent(url.searchParams.get("tag") || "")}${url.searchParams.get("category_id") ? `&category_id=${url.searchParams.get("category_id")}` : ""}${url.searchParams.get("subcategory_id") ? `&subcategory_id=${url.searchParams.get("subcategory_id")}` : ""}${url.searchParams.get("author") ? `&author=${url.searchParams.get("author")}` : ""}` : null,
            } as T;
          }
        } catch {
          // Supabase만 사용 — Django 목록 API로 넘기지 않음
        }
        return {
          count: 0,
          results: [],
          next: null,
          previous: null,
        } as T;
      }
    }

    // Supabase 세션 토큰 우선 → 없으면 localStorage 폴백
    let token: string | null = null;
    try {
      const { supabase: sb } = await import('./supabase');
      if (sb) {
        const { data: { session } } = await sb.auth.getSession();
        if (session?.access_token) {
          token = session.access_token;
        }
      }
    } catch {
      // Supabase 미설정 시 무시
    }
    if (!token) {
      try {
        token = localStorage.getItem('access_token');
      } catch {
        token = null;
      }
    }

    const isFormData =
      typeof FormData !== "undefined" && options.body instanceof FormData;

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };

    // FormData는 브라우저가 boundary 포함 Content-Type을 설정해야 함
    if (!isFormData) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // 백엔드 next URL이 잘못된 호스트(예: foreducator.com2)를 쓰면 path만 추출해 baseURL로 요청
    // 페이지 번호만 오면 baseURL+"2" → ...com2 가 되므로, path는 반드시 "/" 로 시작하거나 http 로 시작
    const pathOrUrl =
      endpoint.startsWith("http")
        ? normalizePaginationNextUrl(endpoint)
        : endpoint.startsWith("/")
          ? endpoint
          : `/${endpoint}`;
    const requestUrl = endpoint.startsWith("http")
      ? this.baseURL.replace(/\/$/, "") + pathOrUrl
      : `${this.baseURL.replace(/\/$/, "")}${pathOrUrl}`;
    emitDjangoDependency({
      kind: "api-request",
      url: requestUrl,
      endpoint,
      method,
      at: new Date().toISOString(),
    });

    const response = await fetch(requestUrl, {
      ...options,
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 401) {
        // 토큰 만료 시 리프레시 시도
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // 재시도
          return this.request<T>(endpoint, options);
        }
        // 리프레시 실패 시 토큰 정리
        try {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        } catch {
          // localStorage 접근 실패 시 무시
        }

        // 토큰이 있었던 경우, 토큰 없이 한 번 더 시도 (public API 대응)
        if (token) {
          console.log('토큰 없이 재시도 중...');
          return this.request<T>(endpoint, options);
        }

        throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
      }

      // 404 등 상태를 상위에서 구분할 수 있도록 status 포함
      if (response.status === 404) {
        const err = new Error('NOT_FOUND');
        (err as any).status = 404;
        throw err;
      }

      // 500 에러 등 서버 에러 처리
      if (response.status >= 500) {
        const errorText = await response.text().catch(() => '');
        let errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorJson.detail || errorMessage;
        } catch {
          // JSON 파싱 실패 시 기본 메시지 사용
        }
        throw new Error(errorMessage);
      }

      const error = await response.json().catch(() => ({ message: '요청에 실패했습니다.' }));
      throw new Error(error.message || error.detail || error.error || '요청에 실패했습니다.');
    }

    // 응답이 비어있는 경우 대비
    const text = await response.text();
    if (!text) return undefined as T;

    // HTML 응답인 경우 (예: 로그인 페이지로 리다이렉트)
    if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      throw new Error('Received HTML response instead of JSON. Authentication may have failed.');
    }

    try {
      const parsed = JSON.parse(text) as T;
      return fixBadHostInValue(parsed) as T;
    } catch (e) {
      console.error('Failed to parse JSON response:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  }

  async refreshToken(): Promise<boolean> {
    // Supabase Auth로 토큰 갱신 (Django SimpleJWT는 더 이상 사용하지 않음)
    try {
      const { supabase } = await import('./supabase');
      if (supabase) {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data.session?.access_token) {
          try {
            localStorage.setItem('access_token', data.session.access_token);
            if (data.session.refresh_token) {
              localStorage.setItem('refresh_token', data.session.refresh_token);
            }
          } catch {
            console.warn('localStorage에 토큰을 저장할 수 없습니다.');
            return false;
          }
          return true;
        }
      }
    } catch {
      // Supabase 미설정·실패
    }
    return false;
  }

  /**
   * Supabase access_token으로 Django user 매핑 보장 (Vercel Function 호출).
   * SimpleJWT를 발급하지 않음 — Supabase JWT를 그대로 사용.
   */
  async ensureSupabaseSession(accessToken: string): Promise<{
    user: { id: number; username: string; email: string; first_name?: string; last_name?: string; is_staff?: boolean; is_superuser?: boolean };
  }> {
    const res = await fetch('/api/auth/ensure-supabase-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: accessToken }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || '세션 연결에 실패했습니다.');
    }
    return res.json();
  }

  // getMeProfile, updateMeProfile, updateMeProfileMultipart, deleteAccount 
  // 등은 현재 모두 src/lib/supabaseProfile.ts 및 Vercel Function으로 이전되어 사용되지 않습니다.
  // Django 의존성 완전 제거를 위해 api.ts에서는 삭제되었습니다.

  async listNotifications(params?: { limit?: number; unreadOnly?: boolean }): Promise<NotificationsResponse> {
    try {
      const { listNotificationsFromSupabase } = await import('./supabaseClassmate');
      const supabaseData = await listNotificationsFromSupabase(params);

      if (supabaseData) {
        return supabaseData as NotificationsResponse;
      }
    } catch (err) {
      console.warn('Supabase Notifications fetch failed:', err);
    }

    // Supabase만 사용 (Django 알림 API 미경유). 실패·미설정 시 빈 목록으로 UI 유지.
    return { unread_count: 0, results: [] };
  }

  async markNotificationRead(notificationId: number): Promise<{ success: boolean; unread_count: number }> {
    try {
      const { markNotificationReadFromSupabase } = await import('./supabaseClassmate');
      const result = await markNotificationReadFromSupabase(notificationId);
      if (result) return result;
    } catch (err) {
      console.warn('Supabase markNotificationRead failed:', err);
    }

    throw new Error('알림 읽음 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.');
  }

  async markAllNotificationsRead(): Promise<{ success: boolean; unread_count: number }> {
    try {
      const { markAllNotificationsReadFromSupabase } = await import('./supabaseClassmate');
      const result = await markAllNotificationsReadFromSupabase();
      if (result) return result;
    } catch (err) {
      console.warn('Supabase markAllNotificationsRead failed:', err);
    }

    throw new Error('전체 알림 읽음 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.');
  }

  async upsertWebPushSubscription(data: {
    endpoint: string;
    p256dh: string;
    authSecret: string;
    userAgent?: string;
  }): Promise<{ success: boolean }> {
    const { upsertWebPushSubscriptionFromSupabase } = await import("./supabaseClassmate");
    const result = await upsertWebPushSubscriptionFromSupabase(data);
    if (result) return result;
    throw new Error("웹 푸시 구독 등록에 실패했습니다.");
  }

  async deactivateWebPushSubscription(endpoint: string): Promise<{ success: boolean }> {
    const { deactivateWebPushSubscriptionFromSupabase } = await import("./supabaseClassmate");
    const result = await deactivateWebPushSubscriptionFromSupabase(endpoint);
    if (result) return result;
    throw new Error("웹 푸시 구독 해제에 실패했습니다.");
  }

  // 쪽지 관련 API (Supabase common_messages_view + RPC만 사용, Django 미경유)
  async listMessages(params?: { page?: number; limit?: number; folder?: 'inbox' | 'sent' }): Promise<MessagesResponse> {
    const { listMessagesFromSupabase } = await import('./supabaseClassmate');
    const supabaseData = await listMessagesFromSupabase(params);
    if (supabaseData == null) {
      throw new Error('쪽지를 불러오지 못했습니다. 로그인 상태를 확인해 주세요.');
    }
    return supabaseData as MessagesResponse;
  }

  async getMessage(messageId: number): Promise<MessageDetailResponse> {
    const { getMessageFromSupabase } = await import('./supabaseClassmate');
    const msg = await getMessageFromSupabase(messageId);
    if (!msg) {
      const err = new Error('쪽지를 찾을 수 없습니다.');
      (err as { status?: number }).status = 404;
      throw err;
    }
    return msg;
  }

  /**
   * 쪽지 전송. 수신자는 `recipient_id`(검색 선택 권장) 또는 `recipient_username`(정확 일치, Vercel resolve-username).
   */
  async sendMessage(data: {
    recipient_id?: number;
    recipient_username?: string;
    subject: string;
    content: string;
    file?: File;
  }): Promise<MessageItem> {
    const { sendMessageViaSupabase } = await import('./supabaseClassmate');
    return sendMessageViaSupabase(data);
  }

  async setMessageImportant(messageId: number, important: boolean): Promise<{ success: boolean }> {
    const { setMessageImportantViaSupabase } = await import('./supabaseMessages');
    await setMessageImportantViaSupabase(messageId, important);
    return { success: true };
  }

  async getMessageAttachmentUrl(path: string): Promise<string | null> {
    const { getMessageAttachmentSignedUrl } = await import('./supabaseMessages');
    return getMessageAttachmentSignedUrl(path);
  }

  async markMessageRead(messageId: number): Promise<{ success: boolean }> {
    const { markMessageReadViaSupabase } = await import('./supabaseClassmate');
    await markMessageReadViaSupabase(messageId);
    return { success: true };
  }

  async deleteMessage(messageId: number): Promise<{ success: boolean }> {
    const { deleteMessageViaSupabase } = await import('./supabaseClassmate');
    await deleteMessageViaSupabase(messageId);
    return { success: true };
  }

  // Workmate: 생기부 작성 도우미 (Vercel Functions 직접 호출)
  async studentRecordStats(): Promise<StudentRecordStatsResponse> {
    return this.workmateRequest<StudentRecordStatsResponse>('/api/workmate/student-record/stats/', {
      method: 'GET',
    });
  }

  async studentRecordGenerate(data: {
    button_type: string;
    user_input: string;
    target_byte_count?: number;
  }): Promise<StudentRecordGenerateResponse> {
    return this.workmateRequest<StudentRecordGenerateResponse>('/api/workmate/student-record/generate/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async studentRecordRecordsList(params?: { page?: number; page_size?: number }): Promise<StudentRecordRecordsResponse> {
    const sp = new URLSearchParams();
    if (params?.page != null) sp.set('page', String(params.page));
    if (params?.page_size != null) sp.set('page_size', String(params.page_size));
    const q = sp.toString();
    return this.workmateRequest<StudentRecordRecordsResponse>(`/api/workmate/student-record/records/${q ? `?${q}` : ''}`, {
      method: 'GET',
    });
  }

  async studentRecordRecordDelete(id: number): Promise<void> {
    return this.workmateRequest<void>(`/api/workmate/student-record/records/${id}/`, {
      method: 'DELETE',
    });
  }

  // Workmate: 평가 계획 작성 (Vercel Functions 직접 호출)
  async evaluationPlanGenerate(data: { user_input: string }): Promise<EvaluationPlanGenerateResponse> {
    return this.workmateRequest<EvaluationPlanGenerateResponse>('/api/workmate/evaluation-plan/generate/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Workmate: 시험 문항 배점 생성기
  async examScoreCalculate(data: {
    total_score: number;
    score_interval: number;
    total_questions: number;
    difficulty_levels: number;
    distribution_method: "uniform-questions" | "uniform-difficulty";
  }): Promise<ExamScoreCalculateResponse> {
    const { calculateExamScore } = await import("./examScoreCalc");
    return calculateExamScore(data) as ExamScoreCalculateResponse;
  }

  // Workmate: 맞춤 GPT (Vercel Functions 직접 호출)
  async listCustomGpts(params?: {
    page?: number;
    limit?: number;
    category?: string;
    sort?: "score" | "likes" | "uses" | "-created_at";
    search?: string;
    filter?: "" | "my" | "liked";
  }): Promise<CustomGptListResponse> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 12;
    const category = params?.category ?? "";
    const sort = params?.sort ?? "score";
    const search = params?.search ?? "";
    const filter = params?.filter ?? "";
    return this.workmateRequest<CustomGptListResponse>(
      `/api/workmate/custom-gpt/?page=${page}&limit=${limit}&category=${encodeURIComponent(category)}&sort=${encodeURIComponent(
        sort
      )}&search=${encodeURIComponent(search)}&filter=${encodeURIComponent(filter)}`,
      { method: "GET" }
    );
  }

  async createCustomGpt(data: {
    title: string;
    description: string;
    system_prompt: string;
    category: CustomGptCategory;
    example_prompts: string;
    is_public: boolean;
  }): Promise<CustomGptItem> {
    return this.workmateRequest<CustomGptItem>("/api/workmate/custom-gpt/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getCustomGpt(id: number): Promise<CustomGptItem> {
    return this.workmateRequest<CustomGptItem>(`/api/workmate/custom-gpt/?action=detail&pk=${id}`, { method: "GET" });
  }

  async updateCustomGpt(id: number, data: Partial<Omit<CustomGptItem, "id" | "author">>): Promise<CustomGptItem> {
    return this.workmateRequest<CustomGptItem>(`/api/workmate/custom-gpt/?action=detail&pk=${id}`, { method: "PATCH", body: JSON.stringify(data) });
  }

  async deleteCustomGpt(id: number): Promise<{ success: boolean }> {
    return this.workmateRequest<{ success: boolean }>(`/api/workmate/custom-gpt/?action=detail&pk=${id}`, { method: "DELETE" });
  }

  async likeCustomGpt(id: number): Promise<{ liked: boolean; score: number; total_likes: number; total_dislikes: number }> {
    return this.workmateRequest(`/api/workmate/custom-gpt/?action=like&pk=${id}`, { method: "POST", body: JSON.stringify({}) });
  }

  async dislikeCustomGpt(id: number): Promise<{ disliked: boolean; score: number; total_likes: number; total_dislikes: number }> {
    return this.workmateRequest(`/api/workmate/custom-gpt/?action=dislike&pk=${id}`, { method: "POST", body: JSON.stringify({}) });
  }

  async getCustomGptChat(id: number): Promise<CustomGptChatResponse> {
    return this.workmateRequest<CustomGptChatResponse>(`/api/workmate/custom-gpt/?action=chat&pk=${id}`, { method: "GET" });
  }

  async sendCustomGptMessage(id: number, message: string): Promise<CustomGptSendResponse> {
    return this.workmateRequest<CustomGptSendResponse>(`/api/workmate/custom-gpt/?action=chat&pk=${id}`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
  }

  // Workmate: LaTeX ↔ 한글 수식 변환기
  async latexToHwpConvert(latex: string): Promise<{ hwp: string }> {
    return this.workmateRequest<{ hwp: string }>("/api/workmate/latex-to-hwp/convert/", {
      method: "POST",
      body: JSON.stringify({ latex }),
    });
  }

  async hwpToLatexConvert(hwp: string): Promise<{ latex: string }> {
    return this.workmateRequest<{ latex: string }>("/api/workmate/latex-to-hwp/hwp-to-latex/", {
      method: "POST",
      body: JSON.stringify({ hwp }),
    });
  }

  async runFileInboxAiReview(submissionId: string): Promise<{
    review: {
      id: string;
      submission_id: string;
      campaign_id: string;
      result_text: string;
      model: string;
      source_filename: string;
      created_at: string;
    };
    cached: boolean;
  }> {
    return this.workmateRequest("/api/workmate/file-inbox/ai-review", {
      method: "POST",
      body: JSON.stringify({ submission_id: submissionId }),
    });
  }

  async runFileInboxPublicAiPreview(
    token: string,
    file: File,
  ): Promise<{ result_text: string; source_filename: string; preview_id: string }> {
    const body = new FormData();
    const rawName = file.name || "file";
    const extMatch = rawName.match(/(\.[a-zA-Z0-9]{1,8})$/);
    const safeUploadName = `upload${extMatch ? extMatch[1].toLowerCase() : ""}`;
    body.append("token", token);
    body.append("filename", rawName);
    body.append("file", file, safeUploadName);
    return this.workmateRequest("/api/workmate/file-inbox/public-ai-preview", {
      method: "POST",
      body,
    });
  }

  async attachFileInboxPublicAiPreview(params: {
    token: string;
    submissionId: string;
    previewId: string;
  }): Promise<{ attached: boolean }> {
    return this.workmateRequest("/api/workmate/file-inbox/public-ai-attach", {
      method: "POST",
      body: JSON.stringify({
        token: params.token,
        submission_id: params.submissionId,
        preview_id: params.previewId,
      }),
    });
  }

  // Receipts: Topics (Supabase 직결)
  async listReceiptsTopics(): Promise<ReceiptsTopicsResponse> {
    const { listReceiptsTopicsFromSupabase } = await import("./supabaseReceipts");
    const sbResult = await listReceiptsTopicsFromSupabase();
    if (sbResult) return sbResult;
    throw new Error("영수증 주제 목록을 불러올 수 없습니다.");
  }

  async createReceiptsTopic(data: { name: string; total_budget?: number }): Promise<ReceiptsTopic> {
    const { createReceiptsTopicInSupabase } = await import("./supabaseReceipts");
    const result = await createReceiptsTopicInSupabase({ name: data.name, total_budget: data.total_budget });
    if (result) return result;
    throw new Error("영수증 주제 생성에 실패했습니다.");
  }

  async updateReceiptsTopic(topicId: number, data: { name?: string; total_budget?: number }): Promise<{ success: boolean; topic: ReceiptsTopic }> {
    const { updateReceiptsTopicInSupabase } = await import("./supabaseReceipts");
    const result = await updateReceiptsTopicInSupabase(topicId, data);
    if (result) return { success: true, topic: result };
    throw new Error("영수증 주제 수정에 실패했습니다.");
  }

  async deleteReceiptsTopic(topicId: number): Promise<{ success: boolean }> {
    const { deleteReceiptsTopicInSupabase } = await import("./supabaseReceipts");
    const ok = await deleteReceiptsTopicInSupabase(topicId);
    if (ok) return { success: true };
    throw new Error("영수증 주제 삭제에 실패했습니다.");
  }

  async getReceiptsTopicTransactions(topicId: number): Promise<ReceiptsTopicTransactionsResponse> {
    const { getReceiptsTopicTransactionsFromSupabase } = await import("./supabaseReceipts");
    const result = await getReceiptsTopicTransactionsFromSupabase(topicId);
    if (result) return result;
    throw new Error("거래 내역을 불러올 수 없습니다.");
  }

  // Receipts: Income
  async createReceiptsIncome(topicId: number, data: { amount: number; description: string; date: string; add_to_budget?: boolean }): Promise<IncomeRecord> {
    const { createReceiptsIncomeInSupabase } = await import("./supabaseReceipts");
    const result = await createReceiptsIncomeInSupabase(topicId, data);
    return result as IncomeRecord;
  }

  async updateReceiptsIncome(incomeId: number, data: Partial<{ amount: number; description: string; date: string }>): Promise<IncomeRecord> {
    const { updateReceiptsIncomeInSupabase } = await import("./supabaseReceipts");
    const result = await updateReceiptsIncomeInSupabase(incomeId, data);
    return result as IncomeRecord;
  }

  async deleteReceiptsIncome(incomeId: number): Promise<{ success: boolean }> {
    const { deleteReceiptsIncomeInSupabase } = await import("./supabaseReceipts");
    await deleteReceiptsIncomeInSupabase(incomeId);
    return { success: true };
  }

  async getReceiptsIncome(incomeId: number): Promise<IncomeRecord> {
    const { getReceiptsIncomeFromSupabase } = await import("./supabaseReceipts");
    return await getReceiptsIncomeFromSupabase(incomeId) as IncomeRecord;
  }

  // Receipts: Receipt
  async createReceiptManual(topicId: number, data: {
    date: string;
    amount: number;
    vendor: string;
    address?: string;
    business_number?: string;
    description?: Array<{ name?: string; unit_price?: number; quantity?: number; amount?: number }>;
  }): Promise<ReceiptRecord> {
    const { createReceiptManualInSupabase } = await import("./supabaseReceipts");
    const result = await createReceiptManualInSupabase(topicId, data);
    if (result) return result;
    throw new Error("지출 수동 등록에 실패했습니다.");
  }

  async uploadReceiptImage(topicId: number, file: File): Promise<{ receipt: ReceiptRecord; processing: boolean }> {
    const { uploadReceiptImageToSupabase } = await import("./supabaseReceipts");
    return await uploadReceiptImageToSupabase(topicId, file);
  }

  async getReceipt(receiptId: number): Promise<ReceiptRecord> {
    const { getReceiptsReceiptFromSupabase } = await import("./supabaseReceipts");
    return await getReceiptsReceiptFromSupabase(receiptId) as ReceiptRecord;
  }

  async updateReceipt(receiptId: number, data: Partial<{
    date: string;
    vendor: string;
    amount: number;
    address: string;
    business_number: string;
    representative: string;
    phone_number: string;
    card_type: string;
    card_number: string;
    installment_months: number | null;
    description: any;
  }>): Promise<{ success: boolean; receipt: ReceiptRecord }> {
    const { updateReceiptInSupabase } = await import("./supabaseReceipts");
    const result = await updateReceiptInSupabase(receiptId, data);
    return { success: true, receipt: result as ReceiptRecord };
  }

  async deleteReceipt(receiptId: number): Promise<{ success: boolean }> {
    const { deleteReceiptInSupabase } = await import("./supabaseReceipts");
    await deleteReceiptInSupabase(receiptId);
    return { success: true };
  }

  // Receipts: downloads (blob)
  async downloadReceiptsExcel(topicId: number): Promise<Blob> {
    const { exportReceiptsExcelBlobFromSupabase } = await import("./supabaseReceiptsExport");
    const blob = await exportReceiptsExcelBlobFromSupabase(topicId);
    if (blob && blob.size > 0) return blob;
    throw new Error("엑셀 만들기에 실패했습니다.");
  }

  async downloadReceiptsImagesZip(topicId: number): Promise<Blob> {
    const { exportReceiptsImagesZipBlobFromSupabase } = await import("./supabaseReceiptsExport");
    const blob = await exportReceiptsImagesZipBlobFromSupabase(topicId);
    if (blob && blob.size > 0) return blob;
    throw new Error("이미지 파일들을 가져올 수 없습니다. 이미지가 존재하지 않을 수도 있습니다.");
  }

  // Workmate: 논술형 평가 채점 (Vercel /api/workmate/essay-assessments — workmateRequest만 사용)
  async listEssayAssessments(): Promise<{ results: EssayAssessment[] }> {
    return this.workmateRequest<{ results: EssayAssessment[] }>("/api/workmate/essay-assessments", { method: "GET" });
  }

  async createEssayAssessment(data: { title: string; problem: string; grading_criteria: string }): Promise<EssayAssessment> {
    return this.workmateRequest<EssayAssessment>("/api/workmate/essay-assessments", { method: "POST", body: JSON.stringify(data) });
  }

  async getEssayAssessment(assessmentId: number): Promise<EssayAssessment> {
    return this.workmateRequest<EssayAssessment>(`/api/workmate/essay-assessments/${assessmentId}`, { method: "GET" });
  }

  async updateEssayAssessment(assessmentId: number, data: Partial<{ title: string; problem: string; grading_criteria: string }>): Promise<EssayAssessment> {
    return this.workmateRequest<EssayAssessment>(`/api/workmate/essay-assessments/${assessmentId}`, { method: "PATCH", body: JSON.stringify(data) });
  }

  async deleteEssayAssessment(assessmentId: number): Promise<{ success: boolean }> {
    return this.workmateRequest(`/api/workmate/essay-assessments/${assessmentId}`, { method: "DELETE" });
  }

  async listEssayAssessmentRecords(assessmentId: number): Promise<{ results: EssayGradingRecord[] }> {
    return this.workmateRequest<{ results: EssayGradingRecord[] }>(`/api/workmate/essay-assessments/${assessmentId}/records`, { method: "GET" });
  }

  async deleteEssayGradingRecord(assessmentId: number, recordId: number): Promise<{ success: boolean }> {
    return this.workmateRequest<{ success: boolean }>(`/api/workmate/essay-assessments/${assessmentId}/records/${recordId}`, { method: "DELETE" });
  }

  async submitEssayAnswer(assessmentId: number, files: File[]): Promise<{ record: EssayGradingRecord }> {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return this.workmateRequest<{ record: EssayGradingRecord }>(`/api/workmate/essay-assessments/${assessmentId}/submit`, { method: "POST", body: fd });
  }

  // Classmate: 논술형 평가 (Vercel /api/classmate/essay-grading)
  async listClassmateEssayAssessments(): Promise<{ results: ClassmateEssayAssessment[] }> {
    return this.workmateRequest<{ results: ClassmateEssayAssessment[] }>("/api/classmate/essay-grading/assessments", { method: "GET" });
  }

  async createClassmateEssayAssessment(data: {
    title: string;
    problem?: string;
    instructions?: string;
    model_answer?: string;
    grading_method?: string;
    classroom_id?: number | null;
    total_points?: number;
    criteria?: { name: string; description?: string; max_points: number }[];
    problem_images?: File[];
    grading_method_files?: File[];
  }): Promise<ClassmateEssayAssessment> {
    const fd = new FormData();
    fd.append("title", data.title);
    fd.append("problem", data.problem || "");
    if (data.instructions) fd.append("instructions", data.instructions);
    if (data.model_answer) fd.append("model_answer", data.model_answer);
    if (data.grading_method) fd.append("grading_method", data.grading_method);
    if (data.classroom_id != null) fd.append("classroom_id", String(data.classroom_id));
    if (data.total_points != null) fd.append("total_points", String(data.total_points));
    if (data.criteria?.length) fd.append("criteria", JSON.stringify(data.criteria));
    (data.problem_images || []).forEach((f) => fd.append("problem_images", f));
    (data.grading_method_files || []).forEach((f) => fd.append("grading_method_files", f));
    return this.workmateRequest<ClassmateEssayAssessment>("/api/classmate/essay-grading/assessments", {
      method: "POST",
      body: fd,
    });
  }

  async getClassmateEssayAssessment(assessmentId: string): Promise<ClassmateEssayAssessment> {
    return this.workmateRequest<ClassmateEssayAssessment>(`/api/classmate/essay-grading/assessments/${assessmentId}`, { method: "GET" });
  }

  async updateClassmateEssayAssessment(
    assessmentId: string,
    data: Partial<{
      title: string;
      problem: string;
      instructions: string;
      model_answer: string;
      grading_method: string;
      classroom_id: number | null;
      total_points: number;
      criteria: { name: string; description?: string; max_points: number }[];
      problem_images?: File[];
      grading_method_files?: File[];
      remove_problem_image_paths?: string[];
      remove_grading_method_image_paths?: string[];
    }>
  ): Promise<ClassmateEssayAssessment> {
    const hasFiles =
      (data.problem_images?.length ?? 0) > 0 || (data.grading_method_files?.length ?? 0) > 0;
    if (hasFiles) {
      const fd = new FormData();
      if (data.title != null) fd.append("title", data.title);
      if (data.problem != null) fd.append("problem", data.problem);
      if (data.instructions != null) fd.append("instructions", data.instructions);
      if (data.model_answer != null) fd.append("model_answer", data.model_answer);
      if (data.grading_method != null) fd.append("grading_method", data.grading_method);
      if (data.classroom_id !== undefined) {
        fd.append("classroom_id", data.classroom_id == null ? "" : String(data.classroom_id));
      }
      if (data.total_points != null) fd.append("total_points", String(data.total_points));
      if (data.criteria) fd.append("criteria", JSON.stringify(data.criteria));
      if (data.remove_problem_image_paths?.length) {
        fd.append("remove_problem_image_paths", JSON.stringify(data.remove_problem_image_paths));
      }
      if (data.remove_grading_method_image_paths?.length) {
        fd.append(
          "remove_grading_method_image_paths",
          JSON.stringify(data.remove_grading_method_image_paths)
        );
      }
      (data.problem_images || []).forEach((f) => fd.append("problem_images", f));
      (data.grading_method_files || []).forEach((f) => fd.append("grading_method_files", f));
      return this.workmateRequest<ClassmateEssayAssessment>(
        `/api/classmate/essay-grading/assessments/${assessmentId}`,
        { method: "PATCH", body: fd }
      );
    }
    return this.workmateRequest<ClassmateEssayAssessment>(`/api/classmate/essay-grading/assessments/${assessmentId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteClassmateEssayAssessment(assessmentId: string): Promise<{ success: boolean }> {
    return this.workmateRequest(`/api/classmate/essay-grading/assessments/${assessmentId}`, { method: "DELETE" });
  }

  async listClassmateEssaySubmissions(assessmentId: string): Promise<{ results: ClassmateEssaySubmission[] }> {
    return this.workmateRequest<{ results: ClassmateEssaySubmission[] }>(
      `/api/classmate/essay-grading/assessments/${assessmentId}/submissions`,
      { method: "GET" }
    );
  }

  async updateClassmateEssaySubmission(
    assessmentId: string,
    submissionId: string,
    data: Partial<{
      student_id: number | null;
      student_name: string;
      student_number: string;
      match_status: string;
      total_score: number;
      overall_feedback: string;
      criterion_scores: { criterion_id: string; score: number; rationale?: string }[];
    }>
  ): Promise<{ submission: ClassmateEssaySubmission }> {
    return this.workmateRequest<{ submission: ClassmateEssaySubmission }>(
      `/api/classmate/essay-grading/assessments/${assessmentId}/submissions/${submissionId}`,
      { method: "PATCH", body: JSON.stringify(data) }
    );
  }

  async deleteClassmateEssaySubmission(assessmentId: string, submissionId: string): Promise<{ success: boolean }> {
    return this.workmateRequest(
      `/api/classmate/essay-grading/assessments/${assessmentId}/submissions/${submissionId}`,
      { method: "DELETE" }
    );
  }

  async submitClassmateEssayAnswers(
    assessmentId: string,
    files: File[]
  ): Promise<{ results: ClassmateEssaySubmission[]; mode?: string; message?: string }> {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return this.workmateRequest<{ results: ClassmateEssaySubmission[]; mode?: string; message?: string }>(
      `/api/classmate/essay-grading/assessments/${assessmentId}/submit`,
      { method: "POST", body: fd }
    );
  }

  async confirmClassmateEssaySplit(
    assessmentId: string,
    data: {
      submission_id: string;
      students: {
        student_name: string | null;
        student_number: string | null;
        start_page: number;
        end_page: number;
      }[];
    }
  ): Promise<{ results: ClassmateEssaySubmission[]; message?: string }> {
    return this.workmateRequest<{ results: ClassmateEssaySubmission[]; message?: string }>(
      `/api/classmate/essay-grading/assessments/${assessmentId}/confirm-split`,
      { method: "POST", body: JSON.stringify(data) }
    );
  }

  async getClassmateEssayReport(assessmentId: string): Promise<ClassmateEssayReport> {
    return this.workmateRequest<ClassmateEssayReport>(`/api/classmate/essay-grading/assessments/${assessmentId}/report`, { method: "GET" });
  }

  async regradeClassmateEssaySubmission(
    assessmentId: string,
    submissionId: string
  ): Promise<{ submission: ClassmateEssaySubmission; error?: string }> {
    return this.workmateRequest<{ submission: ClassmateEssaySubmission; error?: string }>(
      `/api/classmate/essay-grading/assessments/${assessmentId}/submissions/${submissionId}/regrade`,
      { method: "POST" }
    );
  }

  // Community: 에디터 이미지 업로드 (Supabase Storage만 사용; community/rich-text-editor/)
  async uploadCommunityEditorImage(file: File): Promise<{ url: string }> {
    const { uploadCommunityEditorImageFromSupabase } = await import("./supabaseCommunity");
    const res = await uploadCommunityEditorImageFromSupabase(file);
    if (res?.url) return res;
    throw new Error("이미지 업로드에 실패했습니다. 잠시 후 다시 시도해주세요.");
  }

  // Community: 게시판
  async listCommunityCategories(): Promise<CommunityCategory[]> {
    const { getCommunityCategoriesFromSupabase } = await import("./supabaseCommunity");
    return await getCommunityCategoriesFromSupabase() as CommunityCategory[];
  }

  async listCommunityPosts(params?: {
    page?: number;
    limit?: number;
    category_id?: number | "";
    subcategory_id?: number | "";
    q?: string;
    sort?: "recent" | "popular" | "hits" | "likes";
    author?: number;
    tag?: string;
  }): Promise<PaginatedResponse<CommunityPostListItem>> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 20;
    const q = params?.q ?? "";
    const sort = params?.sort ?? "recent";
    const category_id = params?.category_id ?? "";
    const subcategory_id = params?.subcategory_id ?? "";
    const author = params?.author;
    const tag = params?.tag ?? "";
    const empty: PaginatedResponse<CommunityPostListItem> = {
      count: 0,
      results: [],
      next: null,
      previous: null,
    };
    try {
      const { listCommunityPostsFromSupabase } = await import("./supabaseCommunity");
      const data = await listCommunityPostsFromSupabase({
        page,
        limit,
        category_id,
        subcategory_id,
        sort,
        q,
        tag,
        author,
      });
      if (data) {
        const hasMore = page * limit < data.count;
        return {
          count: data.count,
          results: data.results as CommunityPostListItem[],
          next: hasMore ? `${this.baseURL}/api/community/posts/?page=${page + 1}&limit=${limit}&q=${encodeURIComponent(q)}&sort=${sort}&tag=${encodeURIComponent(tag)}${category_id ? `&category_id=${category_id}` : ""}${subcategory_id ? `&subcategory_id=${subcategory_id}` : ""}${author ? `&author=${author}` : ""}` : null,
          previous: page > 1 ? `${this.baseURL}/api/community/posts/?page=${page - 1}&limit=${limit}&q=${encodeURIComponent(q)}&sort=${sort}&tag=${encodeURIComponent(tag)}${category_id ? `&category_id=${category_id}` : ""}${subcategory_id ? `&subcategory_id=${subcategory_id}` : ""}${author ? `&author=${author}` : ""}` : null,
        };
      }
    } catch {
      // Supabase만 사용
    }
    return empty;
  }

  async getCommunityPost(postId: number): Promise<CommunityPostDetail> {
    try {
      const { getCommunityPostDetailFromSupabase, incrementCommunityPostHit } = await import("./supabaseCommunity");
      const data = await getCommunityPostDetailFromSupabase(postId);
      if (data) {
        incrementCommunityPostHit(postId).catch(() => { });
        return data as CommunityPostDetail;
      }
    } catch (e) {
      console.warn("getCommunityPost Supabase failed:", e);
    }
    throw new Error("게시글을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
  }

  async createCommunityPost(data: {
    title: string;
    content: string;
    content_blocks?: CommunityContentBlock[];
    category_id?: number | "";
    subcategory_id?: number | "";
    tags?: string;
    top_fixed?: boolean;
    head_image?: File | null;
    file_upload?: File | null;
    additional_files?: File[];
    vibe_coding_link?: string | null;
  }): Promise<CommunityPostDetail> {
    try {
      const { createCommunityPostFromSupabase } = await import("./supabaseCommunity");
      const res = await createCommunityPostFromSupabase({
        title: data.title,
        content: data.content,
        content_blocks: data.content_blocks,
        category_new_id: data.category_id ? Number(data.category_id) : null,
        subcategory_id: data.subcategory_id ? Number(data.subcategory_id) : null,
        tags: data.tags,
        top_fixed: data.top_fixed,
        head_image: data.head_image,
        file_upload: data.file_upload,
        additional_files: data.additional_files,
        vibe_coding_link: data.vibe_coding_link,
      });
      if (res) return { id: res.id } as CommunityPostDetail;
    } catch (e) {
      console.error("Supabase create post failed:", e);
      if (e instanceof Error) throw e;
    }

    throw new Error("게시글을 등록하지 못했습니다. 로그인·네트워크 상태를 확인한 뒤 다시 시도해 주세요.");
  }

  async updateCommunityPost(
    postId: number,
    data: Partial<{
      title: string;
      content: string;
      content_blocks: CommunityContentBlock[];
      category_id: number | "";
      subcategory_id: number | "";
      tags: string;
      top_fixed: boolean;
      head_image: File | null;
      file_upload: File | null;
      additional_files: File[];
      vibe_coding_link: string | null;
    }>
  ): Promise<CommunityPostDetail> {
    try {
      const { updateCommunityPostFromSupabase } = await import("./supabaseCommunity");
      const res = await updateCommunityPostFromSupabase(postId, {
        title: data.title,
        content: data.content,
        content_blocks: data.content_blocks,
        category_new_id: data.category_id ? Number(data.category_id) : undefined,
        subcategory_id: data.subcategory_id ? Number(data.subcategory_id) : undefined,
        tags: data.tags,
        top_fixed: data.top_fixed,
        head_image: data.head_image,
        file_upload: data.file_upload,
        additional_files: data.additional_files,
        vibe_coding_link: data.vibe_coding_link,
      });
      if (res && res.success) return { id: postId } as CommunityPostDetail;
    } catch (e) {
      console.error("Supabase update post failed:", e);
      if (e instanceof Error) throw e;
    }

    throw new Error("게시글을 수정하지 못했습니다. 잠시 후 다시 시도해 주세요.");
  }

  async deleteCommunityPost(postId: number): Promise<{ success: boolean }> {
    try {
      const { deleteCommunityPostFromSupabase } = await import("./supabaseCommunity");
      const res = await deleteCommunityPostFromSupabase(postId);
      if (res && res.success) return res;
    } catch (e) {
      console.warn("deleteCommunityPost Supabase failed:", e);
    }
    throw new Error("게시글을 삭제하지 못했습니다. 잠시 후 다시 시도해 주세요.");
  }

  async toggleCommunityPostLike(postId: number): Promise<{ liked: boolean; num_likes: number }> {
    try {
      const { toggleCommunityPostLikeFromSupabase } = await import("./supabaseCommunity");
      const res = await toggleCommunityPostLikeFromSupabase(postId);
      if (res) return res;
    } catch (e) {
      console.warn("toggleCommunityPostLike Supabase failed:", e);
    }
    throw new Error("좋아요 처리에 실패했습니다. 로그인을 다시 한 뒤 재시도해 주세요.");
  }

  async toggleCommunityCommentLike(commentId: number): Promise<{ liked: boolean; num_likes: number }> {
    try {
      const { toggleCommunityCommentLikeFromSupabase } = await import("./supabaseCommunity");
      const res = await toggleCommunityCommentLikeFromSupabase(commentId);
      if (res) return res;
    } catch (e) {
      console.warn("toggleCommunityCommentLike Supabase failed:", e);
    }
    throw new Error("댓글 좋아요 처리에 실패했습니다. 로그인을 다시 한 뒤 재시도해 주세요.");
  }

  async toggleCommunityPostBookmark(postId: number): Promise<{ bookmarked: boolean; my_bookmark_count: number }> {
    const { toggleCommunityPostBookmarkFromSupabase } = await import("./supabaseCommunity");
    const res = await toggleCommunityPostBookmarkFromSupabase(postId);
    if (res) return res;
    throw new Error("북마크 처리에 실패했습니다. 로그인을 다시 한 뒤 재시도해주세요.");
  }

  async reportCommunityPost(postId: number, reason?: string): Promise<{ reported: boolean }> {
    try {
      const { reportCommunityPostFromSupabase } = await import("./supabaseCommunity");
      const res = await reportCommunityPostFromSupabase(postId, reason || "");
      if (res) return res;
    } catch (e) {
      console.warn("reportCommunityPost Supabase failed:", e);
    }
    throw new Error("신고 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.");
  }

  async reportCommunityComment(commentId: number, reason?: string): Promise<{ reported: boolean }> {
    try {
      const { reportCommunityCommentFromSupabase } = await import("./supabaseCommunity");
      const res = await reportCommunityCommentFromSupabase(commentId, reason || "");
      if (res) return res;
    } catch (e) {
      console.warn("reportCommunityComment Supabase failed:", e);
    }
    throw new Error("댓글 신고에 실패했습니다. 잠시 후 다시 시도해 주세요.");
  }

  async toggleCommunityBlock(userId: number): Promise<{ blocked: boolean }> {
    try {
      const { toggleCommunityUserBlockFromSupabase } = await import("./supabaseCommunity");
      const res = await toggleCommunityUserBlockFromSupabase(userId);
      if (res) return res;
    } catch (e) {
      console.warn("toggleCommunityBlock Supabase failed:", e);
    }
    throw new Error("차단 처리에 실패했습니다. 로그인 상태를 확인한 뒤 다시 시도해 주세요.");
  }

  /** 게시글 작성자 프로필 카드용 (닉네임, 소개, 팔로우 수, 포인트, 메모, 팔로우 여부). Supabase만 사용. */
  async getCommunityUserProfile(userId: number): Promise<CommunityUserProfile> {
    const { getCommunityUserProfileFromSupabase } = await import("./supabaseCommunity");
    const data = await getCommunityUserProfileFromSupabase(userId);
    if (!data) throw new Error("프로필을 불러올 수 없습니다.");
    return data as any as CommunityUserProfile;
  }

  async toggleCommunityFollow(userId: number): Promise<{ followed: boolean }> {
    try {
      const { toggleCommunityUserFollowFromSupabase } = await import("./supabaseCommunity");
      const res = await toggleCommunityUserFollowFromSupabase(userId);
      if (res) return res;
    } catch (e) {
      console.warn("toggleCommunityFollow Supabase failed:", e);
    }
    throw new Error("팔로우 처리에 실패했습니다. 로그인 상태를 확인한 뒤 다시 시도해 주세요.");
  }

  async getCommunityUserFollowers(userId: number): Promise<{
    results: Array<{ id: number; nickname: string; avatar: string | null; username: string; bio: string; memo: { content: string; color: string | null } | null; is_following: boolean }>;
  }> {
    try {
      const { getCommunityUserFollowersFromSupabase } = await import("./supabaseCommunity");
      const res = await getCommunityUserFollowersFromSupabase(userId);
      if (res) return res as any;
    } catch (e) {
      console.warn("getCommunityUserFollowers Supabase failed:", e);
    }
    return { results: [] };
  }

  async getCommunityUserFollowing(userId: number): Promise<{
    results: Array<{ id: number; nickname: string; avatar: string | null; username: string; bio: string; memo: { content: string; color: string | null } | null; is_following: boolean }>;
  }> {
    try {
      const { getCommunityUserFollowingFromSupabase } = await import("./supabaseCommunity");
      const res = await getCommunityUserFollowingFromSupabase(userId);
      if (res) return res as any;
    } catch (e) {
      console.warn("getCommunityUserFollowing Supabase failed:", e);
    }
    return { results: [] };
  }

  async getCommunityUserMemo(userId: number): Promise<{ content: string | null; color: string | null }> {
    const { getCommunityUserMemoFromSupabase } = await import("./supabaseCommunity");
    const res = await getCommunityUserMemoFromSupabase(userId);
    if (res === null) throw new Error("메모를 불러올 수 없습니다. 로그인 후 이용해 주세요.");
    return res;
  }

  async setCommunityUserMemo(userId: number, data: { content: string; color?: string }): Promise<{ content: string; color: string }> {
    const { setCommunityUserMemoFromSupabase } = await import("./supabaseCommunity");
    return (await setCommunityUserMemoFromSupabase(userId, data)) as { content: string; color: string };
  }

  async listCommunityBlocks(): Promise<{ results: CommunityBlockedUser[] }> {
    const { listCommunityBlocksFromSupabase } = await import("./supabaseCommunity");
    const res = await listCommunityBlocksFromSupabase();
    if (res) return res as any;
    return { results: [] };
  }

  async getCommunityHighlights(): Promise<CommunityHighlightsResponse> {
    const empty: CommunityHighlightsResponse = { latest: [], popular: [], following: [] };
    try {
      const { listCommunityPostsFromSupabase, listFollowingCommunityPostsFromSupabase } = await import("./supabaseCommunity");
      const { supabase } = await import("./supabase");
      
      let userId: string | undefined;
      if (supabase) {
        const session = await supabase.auth.getSession();
        userId = session.data.session?.user?.id;
      }

      const promises: Promise<any>[] = [
        listCommunityPostsFromSupabase({
          page: 1,
          limit: 10,
          sort: "recent",
          chronologicalLatest: true,
        }),
        listCommunityPostsFromSupabase({ page: 1, limit: 10, sort: "popular" })
      ];

      if (userId) {
        promises.push(listFollowingCommunityPostsFromSupabase(userId, 10));
      }

      const results = await Promise.allSettled(promises);
      
      const latestRes = results[0].status === 'fulfilled' ? results[0].value : null;
      const popularRes = results[1].status === 'fulfilled' ? results[1].value : null;
      const followingRes = userId && results[2]?.status === 'fulfilled' ? results[2].value : null;

      if (latestRes || popularRes || followingRes) {
        return {
          latest: (latestRes?.results ?? []) as CommunityPostListItem[],
          popular: (popularRes?.results ?? []) as CommunityPostListItem[],
          following: (followingRes?.results ?? []) as CommunityPostListItem[],
        };
      }
    } catch { /* Supabase 실패 시 빈 하이라이트 */ }

    return empty;
  }

  async listCommunityComments(postId: number): Promise<CommunityComment[]> {
    try {
      const { listCommunityCommentsFromSupabase } = await import("./supabaseCommunity");
      const data = await listCommunityCommentsFromSupabase(postId);
      if (data) return normalizeCommunityCommentsList(data as CommunityComment[]);
    } catch (e) {
      console.warn("listCommunityComments Supabase failed:", e);
    }
    throw new Error("댓글을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
  }

  async createCommunityComment(postId: number, data: { content: string; parent_id?: number | null; comment_image?: File | null; comment_file?: File | null }): Promise<CommunityComment> {
    try {
      const { createCommunityCommentFromSupabase } = await import("./supabaseCommunity");
      const res = await createCommunityCommentFromSupabase(postId, data);
      if (res) return normalizeCommunityComment(res as CommunityCommentWithDbPaths);
    } catch (e) {
      console.warn("createCommunityComment Supabase failed:", e);
    }
    throw new Error("댓글을 등록하지 못했습니다. 잠시 후 다시 시도해 주세요.");
  }

  async updateCommunityComment(commentId: number, data: { content?: string; comment_image?: File | null; comment_file?: File | null }): Promise<CommunityComment> {
    try {
      const { updateCommunityCommentFromSupabase } = await import("./supabaseCommunity");
      const res = await updateCommunityCommentFromSupabase(commentId, data);
      if (res) return normalizeCommunityComment(res as CommunityCommentWithDbPaths);
    } catch (e) {
      console.warn("updateCommunityComment Supabase failed:", e);
    }
    throw new Error("댓글을 수정하지 못했습니다. 변경할 내용이 있는지 확인한 뒤 다시 시도해 주세요.");
  }

  async deleteCommunityComment(commentId: number): Promise<{ success: boolean }> {
    try {
      const { deleteCommunityCommentFromSupabase } = await import("./supabaseCommunity");
      const res = await deleteCommunityCommentFromSupabase(commentId);
      if (res && res.success) return res;
    } catch (e) {
      console.warn("deleteCommunityComment Supabase failed:", e);
    }
    throw new Error("댓글을 삭제하지 못했습니다. 잠시 후 다시 시도해 주세요.");
  }

  async deleteCommunityPostFile(fileId: number): Promise<{ success: boolean }> {
    try {
      const { supabase } = await import("./supabase");
      if (supabase) {
        const { error } = await supabase.from('tboard_postfile').delete().eq('id', fileId);
        if (!error) return { success: true };
      }
    } catch (e) {
      console.warn("deleteCommunityPostFile Supabase failed:", e);
    }
    throw new Error("첨부파일을 삭제하지 못했습니다. 권한과 네트워크를 확인한 뒤 다시 시도해 주세요.");
  }

  async listMyCommunityPosts(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<CommunityPostListItem>> {
    try {
      const { listMyCommunityPostsFromSupabase } = await import("./supabaseCommunity");
      const res = await listMyCommunityPostsFromSupabase(params || {});
      if (res) return res as any as PaginatedResponse<CommunityPostListItem>;
    } catch (e) {
      console.warn("listMyCommunityPosts Supabase failed:", e);
    }
    return { results: [], count: 0, next: null, previous: null } as PaginatedResponse<CommunityPostListItem>;
  }

  async listMyCommunityLikes(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<CommunityPostListItem>> {
    const { listMyCommunityLikesFromSupabase } = await import("./supabaseCommunity");
    const res = await listMyCommunityLikesFromSupabase(params || {});
    if (res) return res as any as PaginatedResponse<CommunityPostListItem>;
    return { results: [], count: 0, next: null, previous: null };
  }

  async listMyCommunityCommented(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<CommunityPostListItem>> {
    const { listMyCommunityCommentedFromSupabase } = await import("./supabaseCommunity");
    const res = await listMyCommunityCommentedFromSupabase(params || {});
    if (res) return res as any as PaginatedResponse<CommunityPostListItem>;
    return { results: [], count: 0, next: null, previous: null };
  }

  async listMyCommunityBookmarks(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<CommunityPostListItem>> {
    const { listMyCommunityBookmarksFromSupabase } = await import("./supabaseCommunity");
    const res = await listMyCommunityBookmarksFromSupabase(params || {});
    if (res) return res as any as PaginatedResponse<CommunityPostListItem>;
    return { results: [], count: 0, next: null, previous: null };
  }

  // Informate: 교사들의 책
  async downloadInformateBooksExcel(): Promise<{ blob: Blob; filename: string }> {
    const token = localStorage.getItem("access_token");
    const resp = await fetch(`/api/informate/books/download-excel`, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) throw new Error("엑셀 다운로드에 실패했습니다.");

    const cd = resp.headers.get("content-disposition") || resp.headers.get("Content-Disposition") || "";
    let filename = "books_list.xlsx";
    const mStar = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    const m = cd.match(/filename\s*=\s*("?)([^\\";]+)\1/i);
    if (mStar?.[1]) {
      try { filename = decodeURIComponent(mStar[1].trim()); } catch { filename = mStar[1].trim(); }
    } else if (m?.[2]) {
      filename = m[2].trim();
    }
    return { blob: await resp.blob(), filename };
  }

  async listInformateBooks(params?: { page?: number; limit?: number; sort?: "recommended" | "publication" | "recent"; search?: string }): Promise<InformateBooksListResponse> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 18;
    const sort = params?.sort ?? "recommended";
    const search = params?.search ?? "";

    const { fetchInformateBooksFromSupabase } = await import("./supabaseInformate");
    const res = await fetchInformateBooksFromSupabase(page, limit, sort, search);
    if (!res) throw new Error("도서 목록을 불러오지 못했습니다. (Supabase)");
    return res as any;
  }

  async listInformatePendingBooks(params?: { page?: number; limit?: number }): Promise<InformateAdminBooksListResponse> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 18;

    const { listInformatePendingBooksFromSupabase } = await import("./supabaseInformate");
    const res = await listInformatePendingBooksFromSupabase(page, limit);
    if (!res) throw new Error("승인 대기 목록을 불러오지 못했습니다. (Supabase)");
    return res as any;
  }

  async approveInformateBook(bookId: number): Promise<{
    success: boolean;
    book?: InformateAdminBookItem;
    warning?: string;
    community_post_id?: number | null;
    community_post_created?: boolean;
  }> {
    const token = localStorage.getItem("access_token");
    const resp = await fetch(`/api/informate/books/${bookId}/approve`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => null);
      throw new Error(body?.error || "Approval failed");
    }
    return resp.json();
  }

  async rejectInformateBook(bookId: number): Promise<{ success: boolean; book: InformateAdminBookItem }> {
    const token = localStorage.getItem("access_token");
    const resp = await fetch(`/api/informate/books/${bookId}/reject`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => null);
      throw new Error(body?.error || "Rejection failed");
    }
    return resp.json() as Promise<{ success: boolean; book: InformateAdminBookItem }>;
  }

  async deleteInformateBook(bookId: number): Promise<{ success: boolean; id: number }> {
    const token = localStorage.getItem("access_token");
    const resp = await fetch(`/api/informate/books/${bookId}/delete`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => null);
      throw new Error(body?.error || "도서 삭제에 실패했습니다.");
    }
    return resp.json() as Promise<{ success: boolean; id: number }>;
  }

  async getInformateBook(bookId: number): Promise<InformateBookItem> {
    const { getInformateBookFromSupabase } = await import("./supabaseInformate");
    const res = await getInformateBookFromSupabase(bookId);
    if (!res) throw new Error("도서 상세 정보를 불러오지 못했습니다. (Supabase)");
    return res as any;
  }

  async recommendInformateBook(bookId: number): Promise<{ recommended: boolean; recommendation_count: number }> {
    const { toggleInformateBookRecommendFromSupabase } = await import("./supabaseInformate");
    return toggleInformateBookRecommendFromSupabase(bookId);
  }

  async searchBooks(query: string): Promise<{ books: any[]; error?: string }> {
    const token = localStorage.getItem("access_token");
    const resp = await fetch(`/api/informate/books/search?query=${encodeURIComponent(query)}`, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      return { books: [], error: "검색에 실패했습니다." };
    }
    return resp.json();
  }

  async submitBook(data: {
    title: string;
    author: string;
    publisher: string;
    publication_date: string;
    isbn?: string;
    description: string;
    price?: string;
    image_url?: string;
    store_url?: string;
    image?: File;
  }): Promise<{ success: boolean; book_id: number; status: string } | any> {
    const { submitInformateBookFromSupabase } = await import("./supabaseInformate");
    const res = await submitInformateBookFromSupabase(data);
    if (res && res.success) return { success: true, book_id: Date.now(), status: "pending" };
    throw new Error("도서 등록 요청에 실패했습니다. (Supabase)");
  }

  // Informate: 교사들의 유튜브
  async listInformateYoutubers(params?: { page?: number; limit?: number; category?: YoutuberCategory; search?: string }): Promise<YoutubersListResponse> {
    const { listInformateYoutubersFromSupabase } = await import("./supabaseInformate");
    const res = await listInformateYoutubersFromSupabase(params || {});
    if (!res) throw new Error("유튜버 목록을 불러오지 못했습니다.");
    return { ...res, count: res.count, results: res.results as any } as YoutubersListResponse;
  }

  async getInformateYoutuber(youtuberId: number): Promise<YoutuberItem> {
    const { getInformateYoutuberFromSupabase, listInformateYoutuberPostsAllFromSupabase } = await import("./supabaseInformate");
    const row = await getInformateYoutuberFromSupabase(youtuberId);
    if (!row) throw new Error("유튜버 정보를 찾을 수 없습니다.");
    
    const postsRes = await listInformateYoutuberPostsAllFromSupabase({ page: 1, limit: 3, youtuber_id: youtuberId });
    const posts: YoutuberPostItem[] = postsRes?.results || [];

    return {
      id: row.id,
      user: row.user,
      youtube_id: row.youtube_id,
      latest_post_date: row.latest_post_date,
      name: row.name,
      affiliation: row.affiliation,
      introduction: row.introduction,
      thumbnail_image_url: row.thumbnail_image_url,
      school_type: row.school_type as YoutuberSchoolType,
      school_type_display: "",
      subject: row.subject || "",
      last_updated: row.last_updated,
      likes_count: row.likes_count,
      is_liked: row.is_liked,
      recommendation_count: row.recommendation_count,
      is_recommended: false,
      posts,
    };
  }

  async toggleInformateYoutuberLike(youtuberId: number): Promise<{ liked: boolean; likes_count: number }> {
    const { toggleInformateCreatorLikeFromSupabase } = await import("./supabaseInformate");
    const res = await toggleInformateCreatorLikeFromSupabase("youtuber", youtuberId);
    if (!res) throw new Error("처리에 실패했습니다.");
    
    return { liked: res.liked, likes_count: res.likes_count };
  }

  async listInformateYoutuberPosts(youtuberId: number, params?: { page?: number; limit?: number }): Promise<YoutuberPostsListResponse> {
    const { listInformateYoutuberPostsAllFromSupabase } = await import("./supabaseInformate");
    const res = await listInformateYoutuberPostsAllFromSupabase({ ...params, youtuber_id: youtuberId });
    if (!res) throw new Error("목록을 불러오지 못했습니다.");
    return res as unknown as YoutuberPostsListResponse;
  }

  async listInformateYoutuberPostsAll(params?: { page?: number; limit?: number; youtuber_id?: number }): Promise<YoutuberPostsListResponse> {
    const { listInformateYoutuberPostsAllFromSupabase } = await import("./supabaseInformate");
    const res = await listInformateYoutuberPostsAllFromSupabase(params || {});
    if (!res) throw new Error("목록을 불러오지 못했습니다.");
    return res as unknown as YoutuberPostsListResponse;
  }

  async deleteInformateYoutuber(creatorId: number): Promise<{ success: boolean }> {
    const { deleteInformateCreatorFromSupabase } = await import("./supabaseInformate");
    return deleteInformateCreatorFromSupabase("youtuber", creatorId);
  }

  async getMyInformateYoutuber(): Promise<YoutuberItem> {
    const { getMyInformateCreatorFromSupabase } = await import("./supabaseInformate");
    const item = await getMyInformateCreatorFromSupabase("youtuber");
    return { ...item, youtube_id: (item as any).youtube_id, posts: [], school_type_display: "", is_liked: false, likes_count: 0, recommendation_count: 0, is_recommended: false } as YoutuberItem;
  }

  async createInformateYoutuber(data: {
    youtube_id: string; name?: string; affiliation?: string; introduction?: string;
    thumbnail_image?: File; school_type: YoutuberSchoolType; subject?: string;
  }): Promise<YoutuberItem> {
    const { submitInformateYoutuberFromSupabase } = await import("./supabaseInformate");
    const res = await submitInformateYoutuberFromSupabase({
      ...data,
      name: data.name || "",
      introduction: data.introduction || ""
    });
    if (!res) throw new Error("등록에 실패했습니다.");
    return this.getMyInformateYoutuber();
  }

  async updateInformateYoutuber(data: Partial<{
    youtube_id: string; name: string; affiliation: string; introduction: string;
    thumbnail_image: File; school_type: YoutuberSchoolType; subject: string;
  }>): Promise<YoutuberItem> {
    const { updateInformateCreatorFromSupabase, getMyInformateCreatorFromSupabase } = await import("./supabaseInformate");
    const me = await getMyInformateCreatorFromSupabase("youtuber");
    await updateInformateCreatorFromSupabase("youtuber", me.id, data);
    return this.getMyInformateYoutuber();
  }

  // Informate: 교사들의 블로그(Bloger)
  async listInformateBlogers(params?: { page?: number; limit?: number; category?: BlogerCategory; search?: string }): Promise<BlogersListResponse> {
    const { listInformateBlogersFromSupabase } = await import("./supabaseInformate");
    const res = await listInformateBlogersFromSupabase(params || {});
    if (!res) throw new Error("목록을 불러오지 못했습니다.");
    return { ...res, count: res.count, results: res.results as any } as BlogersListResponse;
  }

  async getInformateBloger(blogerId: number): Promise<BlogerItem> {
    const { getInformateCreatorFromSupabase } = await import("./supabaseInformate");
    const row = await getInformateCreatorFromSupabase("bloger", blogerId);
    if (!row) throw new Error("정보를 찾을 수 없습니다.");
    return { ...row, naver_id: (row as any).naver_id, school_type_display: "", is_recommended: false, recommendation_count: 0, posts: [] } as BlogerItem;
  }

  async toggleInformateBlogerLike(blogerId: number): Promise<{ liked: boolean; likes_count: number }> {
    const { toggleInformateCreatorLikeFromSupabase } = await import("./supabaseInformate");
    const res = await toggleInformateCreatorLikeFromSupabase("bloger", blogerId);
    if (!res) throw new Error("처리에 실패했습니다.");
    return { liked: res.liked, likes_count: res.likes_count };
  }

  async deleteInformateBloger(creatorId: number): Promise<{ success: boolean }> {
    const { deleteInformateCreatorFromSupabase } = await import("./supabaseInformate");
    return deleteInformateCreatorFromSupabase("bloger", creatorId);
  }

  async getMyInformateBloger(): Promise<BlogerItem> {
    const { getMyInformateCreatorFromSupabase } = await import("./supabaseInformate");
    const item = await getMyInformateCreatorFromSupabase("bloger");
    return { ...item, naver_id: (item as any).naver_id, posts: [], school_type_display: "", is_liked: false, likes_count: 0, recommendation_count: 0, is_recommended: false } as BlogerItem;
  }

  async createInformateBloger(data: {
    naver_id: string; name?: string; affiliation?: string; introduction?: string;
    thumbnail_image?: File; school_type: BlogerSchoolType; subject?: string;
  }): Promise<BlogerItem> {
    const { submitInformateBlogerFromSupabase } = await import("./supabaseInformate");
    const res = await submitInformateBlogerFromSupabase(data);
    if (!res) throw new Error("등록에 실패했습니다.");
    return this.getMyInformateBloger();
  }

  async updateInformateBloger(data: Partial<{
    naver_id: string; name: string; affiliation: string; introduction: string;
    thumbnail_image: File; school_type: BlogerSchoolType; subject: string;
  }>): Promise<BlogerItem> {
    const { updateInformateCreatorFromSupabase, getMyInformateCreatorFromSupabase } = await import("./supabaseInformate");
    const me = await getMyInformateCreatorFromSupabase("bloger");
    await updateInformateCreatorFromSupabase("bloger", me.id, data);
    return this.getMyInformateBloger();
  }

  // Informate: 교사들의 바이브코딩
  async listInformateVibeSiteCategories() {
    const { listInformateVibeCategoriesFromSupabase } = await import("./supabaseInformateVibeSites");
    return listInformateVibeCategoriesFromSupabase();
  }

  async listInformateVibeSiteTags() {
    const { listInformateVibeTagsFromSupabase } = await import("./supabaseInformateVibeSites");
    return listInformateVibeTagsFromSupabase();
  }

  async listInformateVibeSites(params?: {
    page?: number;
    limit?: number;
    sort?: InformateVibeSiteSort;
    search?: string;
    categoryId?: number | null;
    tagId?: number | null;
  }): Promise<InformateVibeSitesListResponse> {
    const { listInformateVibeSitesFromSupabase } = await import("./supabaseInformateVibeSites");
    const res = await listInformateVibeSitesFromSupabase(params || {});
    return { ...res, previous: null };
  }

  async getInformateVibeSite(siteId: number): Promise<InformateVibeSiteItem> {
    const { getInformateVibeSiteFromSupabase } = await import("./supabaseInformateVibeSites");
    const row = await getInformateVibeSiteFromSupabase(siteId);
    if (!row) throw new Error("자료를 찾을 수 없습니다.");
    return row;
  }

  async fetchInformateVibeSitePreview(url: string) {
    const { fetchVibeSitePreview } = await import("./supabaseInformateVibeSites");
    return fetchVibeSitePreview(url);
  }

  async createInformateVibeSiteAuth(data: {
    title: string;
    url: string;
    description: string;
    categoryId: number;
    tagIds: number[];
    previewImageUrl?: string | null;
    previewTitle?: string | null;
  }): Promise<number> {
    const { createInformateVibeSiteAuth } = await import("./supabaseInformateVibeSites");
    return createInformateVibeSiteAuth(data);
  }

  async createInformateVibeSiteGuest(data: {
    title: string;
    url: string;
    description: string;
    categoryId: number;
    password: string;
    guestDisplayName?: string;
    tagIds: number[];
    previewImageUrl?: string | null;
    previewTitle?: string | null;
  }): Promise<{ id: number; manageToken: string }> {
    const { createInformateVibeSiteGuest } = await import("./supabaseInformateVibeSites");
    return createInformateVibeSiteGuest(data);
  }

  async updateInformateVibeSiteAuth(data: {
    id: number;
    title: string;
    url: string;
    description: string;
    categoryId: number;
    tagIds: number[];
    previewImageUrl?: string | null;
    previewTitle?: string | null;
  }): Promise<void> {
    const { updateInformateVibeSiteAuth } = await import("./supabaseInformateVibeSites");
    return updateInformateVibeSiteAuth(data);
  }

  async updateInformateVibeSiteGuest(data: {
    id: number;
    title: string;
    url: string;
    description: string;
    categoryId: number;
    tagIds: number[];
    previewImageUrl?: string | null;
    previewTitle?: string | null;
    password?: string;
    manageToken?: string;
  }): Promise<void> {
    const { updateInformateVibeSiteGuest } = await import("./supabaseInformateVibeSites");
    return updateInformateVibeSiteGuest(data);
  }

  async deleteInformateVibeSiteAuth(id: number): Promise<void> {
    const { deleteInformateVibeSiteAuth } = await import("./supabaseInformateVibeSites");
    return deleteInformateVibeSiteAuth(id);
  }

  async deleteInformateVibeSiteGuest(
    id: number,
    opts: { password?: string; manageToken?: string }
  ): Promise<void> {
    const { deleteInformateVibeSiteGuest } = await import("./supabaseInformateVibeSites");
    return deleteInformateVibeSiteGuest(id, opts);
  }

  async toggleInformateVibeSiteLike(siteId: number): Promise<{ liked: boolean; likes_count: number }> {
    const { toggleInformateVibeSiteLikeFromSupabase } = await import("./supabaseInformateVibeSites");
    return toggleInformateVibeSiteLikeFromSupabase(siteId);
  }

  async listInformateVibeSiteComments(siteId: number): Promise<InformateVibeSiteComment[]> {
    const { listInformateVibeSiteCommentsFromSupabase } = await import("./supabaseInformateVibeSites");
    return listInformateVibeSiteCommentsFromSupabase(siteId);
  }

  async createInformateVibeSiteComment(
    siteId: number,
    body: string,
    parentId?: number | null
  ): Promise<number> {
    const { createInformateVibeSiteComment } = await import("./supabaseInformateVibeSites");
    return createInformateVibeSiteComment(siteId, body, parentId);
  }

  async updateInformateVibeSiteComment(commentId: number, body: string): Promise<void> {
    const { updateInformateVibeSiteComment } = await import("./supabaseInformateVibeSites");
    return updateInformateVibeSiteComment(commentId, body);
  }

  async deleteInformateVibeSiteComment(commentId: number): Promise<void> {
    const { deleteInformateVibeSiteComment } = await import("./supabaseInformateVibeSites");
    return deleteInformateVibeSiteComment(commentId);
  }

  async setInformateVibeSiteHidden(siteId: number, hidden: boolean): Promise<void> {
    const { setInformateVibeSiteHidden } = await import("./supabaseInformateVibeSites");
    return setInformateVibeSiteHidden(siteId, hidden);
  }

  // Informate: 교사 오픈채팅(OpenChats)
  async listInformateOpenChatsCategories(): Promise<OpenChatCategory[]> {
    const { fetchInformateOpenChatCategoriesFromSupabase } = await import("./supabaseInformate");
    const res = await fetchInformateOpenChatCategoriesFromSupabase();
    if (!res) throw new Error("카테고리를 불러오지 못했습니다.");
    return res;
  }

  async listInformateOpenChats(params?: { page?: number; limit?: number; search?: string; category?: string }): Promise<OpenChatsListResponse> {
    const { fetchInformateOpenChatsFromSupabase } = await import("./supabaseInformate");
    const res = await fetchInformateOpenChatsFromSupabase(params || {});
    if (!res) throw new Error("목록을 불러오지 못했습니다.");
    return res;
  }

  async getInformateOpenChatInfo(chatId: number): Promise<OpenChatInfo> {
    const { getInformateOpenChatInfoFromSupabase } = await import("./supabaseInformate");
    const info = await getInformateOpenChatInfoFromSupabase(chatId);
    return { ...info, password: "", tags: info.tags.join(",") } as unknown as OpenChatInfo;
  }

  async createInformateOpenChat(data: { name: string; link: string; description?: string; category: number; password?: string; tags?: string }): Promise<{ result: string; chat_id: number }> {
    const { submitInformateOpenChatFromSupabase } = await import("./supabaseInformate");
    const res = await submitInformateOpenChatFromSupabase(data);
    if (!res) throw new Error("생성에 실패했습니다.");
    return res as any;
  }

  async updateInformateOpenChat(chatId: number, data: Partial<{ name: string; link: string; description: string; category: number; password: string; tags: string }>): Promise<{ result: string }> {
    const { updateInformateOpenChatFromSupabase } = await import("./supabaseInformate");
    return updateInformateOpenChatFromSupabase(chatId, data);
  }

  async deleteInformateOpenChat(chatId: number): Promise<{ result: string }> {
    const { deleteInformateOpenChatFromSupabase } = await import("./supabaseInformate");
    return deleteInformateOpenChatFromSupabase(chatId);
  }

  async listInformateOpenChatPasswords(chatId: number): Promise<{ result: string; passwords: OpenChatPasswordItem[] }> {
    const { listInformateOpenChatPasswordsFromSupabase } = await import("./supabaseInformate");
    return listInformateOpenChatPasswordsFromSupabase(chatId);
  }

  async addInformateOpenChatPassword(chatId: number, password: string): Promise<{ result: string }> {
    const { addInformateOpenChatPasswordFromSupabase } = await import("./supabaseInformate");
    return addInformateOpenChatPasswordFromSupabase(chatId, password);
  }

  async voteInformateOpenChatPassword(chatId: number, passwordId: number, voteType: "up" | "down"): Promise<{ result: string }> {
    const { voteInformateOpenChatPasswordFromSupabase } = await import("./supabaseInformate");
    return voteInformateOpenChatPasswordFromSupabase(chatId, passwordId, voteType);
  }


  // Classmate: Card games — Supabase only (Vercel↔Supabase). User id from localStorage user_data.
  async listClassmateGames(params?: {
    q?: string;
    scope?: "public" | "mine";
    limit?: number;
    offset?: number;
  }): Promise<{
    results: ClassmateGame[];
    count: number;
    limit: number;
    offset: number;
    has_more: boolean;
  }> {
    const scope = params?.scope ?? "public";
    const limit = params?.limit ?? 12;
    const offset = params?.offset ?? 0;
    const q = params?.q?.trim() ?? "";

    const { listClassmateGamesFromSupabase } = await import("./supabaseClassmate");
    const { getMyDjangoUserId } = await import("./supabaseGames");
    const currentUserId = await getMyDjangoUserId();
    const res = await listClassmateGamesFromSupabase({
      q: q || undefined,
      scope,
      limit,
      offset,
      djangoUserId: scope === "mine" ? (currentUserId ?? undefined) : undefined,
    });
    if (!res) {
      if (scope === "mine") {
        throw new Error("로그인이 필요합니다.");
      }
      throw new Error("카드 게임 목록을 불러오지 못했습니다. 네트워크와 Supabase 설정을 확인해 주세요.");
    }

    const results: ClassmateGame[] = res.results.map((row) => ({
      id: row.id,
      title: row.title,
      subject: row.subject ?? "",
      description: row.description ?? "",
      is_public: row.is_public,
      created_at: row.created_at,
      updated_at: row.updated_at,
      card_count: row.card_count,
      is_owner: currentUserId != null && row.user_id === currentUserId,
    }));
    return {
      results,
      count: res.count,
      limit: res.limit,
      offset: res.offset,
      has_more: res.has_more,
    };
  }

  async getClassmateGame(gameId: number): Promise<ClassmateGameDetail> {
    const { getClassmateGameFromSupabase } = await import("./supabaseGames");
    const result = await getClassmateGameFromSupabase(gameId);
    if (!result) throw new Error("Failed to load game from Supabase");
    return result as ClassmateGameDetail;
  }

  async createClassmateGame(data: {
    title: string;
    subject?: string;
    description?: string;
    is_public?: boolean;
    cards: Array<{
      question_text: string;
      answer_text: string;
      question_image?: File | null;
      answer_image?: File | null;
      existing_question_image_url?: string | null;
      existing_answer_image_url?: string | null;
    }>;
  }): Promise<ClassmateGameDetail> {
    const { createClassmateGameFromSupabase } = await import("./supabaseGames");
    
    // Map cards to support upload
    const images: Record<string, File> = {};
    const cards_json = data.cards.map((c, i) => {
      const qKey = c.question_image ? `q_${i}` : undefined;
      const aKey = c.answer_image ? `a_${i}` : undefined;
      if (qKey && c.question_image) images[qKey] = c.question_image;
      if (aKey && c.answer_image) images[aKey] = c.answer_image;
      return {
        question_text: c.question_text,
        answer_text: c.answer_text,
        question_image_key: qKey,
        answer_image_key: aKey,
        existing_question_image_url: c.existing_question_image_url,
        existing_answer_image_url: c.existing_answer_image_url,
      };
    });

    const result = await createClassmateGameFromSupabase({
      title: data.title,
      subject: data.subject,
      description: data.description,
      is_public: data.is_public,
      cards_json,
      images
    });
    if (!result) throw new Error("Failed to create game in Supabase");
    return result as ClassmateGameDetail;
  }

  async deleteClassmateGame(gameId: number): Promise<{ success: boolean }> {
    const { deleteClassmateGameFromSupabase } = await import("./supabaseGames");
    await deleteClassmateGameFromSupabase(gameId);
    return { success: true };
  }

  async updateClassmateGame(
    gameId: number,
    data: {
      title: string;
      subject?: string;
      description?: string;
      is_public?: boolean;
      cards: Array<{
        question_text: string;
        answer_text: string;
        question_image?: File | null;
        answer_image?: File | null;
        existing_question_image_url?: string | null;
        existing_answer_image_url?: string | null;
      }>;
    }
  ): Promise<ClassmateGameDetail> {
    const { updateClassmateGameFromSupabase } = await import("./supabaseGames");

    const images: Record<string, File> = {};
    const cards_json = data.cards.map((c, i) => {
      const qKey = c.question_image ? `q_${i}` : undefined;
      const aKey = c.answer_image ? `a_${i}` : undefined;
      if (qKey && c.question_image) images[qKey] = c.question_image;
      if (aKey && c.answer_image) images[aKey] = c.answer_image;
      return {
        question_text: c.question_text,
        answer_text: c.answer_text,
        question_image_key: qKey,
        answer_image_key: aKey,
        existing_question_image_url: c.existing_question_image_url,
        existing_answer_image_url: c.existing_answer_image_url,
      };
    });

    const result = await updateClassmateGameFromSupabase(gameId, {
      title: data.title,
      subject: data.subject,
      description: data.description,
      is_public: data.is_public,
      cards_json,
      images,
    });
    if (!result) throw new Error("Failed to update game in Supabase");
    return result as ClassmateGameDetail;
  }

  async startClassmateGame(gameId: number, data?: { players?: number; pair_count?: number; include_bonus?: boolean }): Promise<ClassmateGameStartResponse> {
    const { startClassmateGameFromSupabase } = await import("./supabaseGames");
    const result = await startClassmateGameFromSupabase(gameId, data);
    if (!result) throw new Error("Failed to start game in Supabase");
    return result as ClassmateGameStartResponse;
  }

  // Classmate: 퀴즈 배틀
  async getQuizBattleFavoriteIds(): Promise<number[]> {
    const { getQuizBattleFavoriteIdsFromSupabase } = await import("./supabaseClassmate");
    const ids = await getQuizBattleFavoriteIdsFromSupabase();
    if (ids == null) throw new Error("로그인이 필요합니다.");
    return ids;
  }

  async toggleQuizBattleFavorite(
    quizSetId: number
  ): Promise<{ is_favorited: boolean; favorite_count: number }> {
    const { toggleQuizBattleFavoriteFromSupabase } = await import("./supabaseClassmate");
    const result = await toggleQuizBattleFavoriteFromSupabase(quizSetId);
    if (!result) throw new Error("즐겨찾기 저장에 실패했습니다.");
    return result;
  }

  async listQuizBattleSets(params?: {
    q?: string;
    scope?: "public" | "mine" | "favorites";
    limit?: number;
    offset?: number;
    favoriteIds?: number[];
  }): Promise<{
    results: QuizBattleSetItem[];
    count: number;
    limit: number;
    offset: number;
    has_more: boolean;
  }> {
    const scope = params?.scope ?? "public";
    const limit = params?.limit ?? 12;
    const offset = params?.offset ?? 0;
    const q = params?.q?.trim() ?? "";

    const { listClassmateQuizBattlesFromSupabase, getMyDjangoUserId } = await import("./supabaseGames");
    const currentUserId = await getMyDjangoUserId();
    const mapRowToItem = (row: any): QuizBattleSetItem => ({
      id: row.id,
      title: row.title,
      subject: row.subject ?? "",
      description: row.description ?? "",
      is_public: row.is_public,
      created_at: row.created_at,
      updated_at: row.updated_at,
      question_count: row.question_count,
      favorite_count: Math.max(0, Number(row.favorite_count) || 0),
      is_owner: currentUserId != null && row.user_id === currentUserId,
    });

    if (scope === "favorites") {
      if (currentUserId == null) throw new Error("로그인이 필요합니다.");
      const favorites = new Set(normalizeQuizBattleFavoriteIds(params?.favoriteIds));
      if (!favorites.size) {
        return { results: [], count: 0, limit, offset, has_more: false };
      }

      const pullRows = async (targetScope: "public" | "mine"): Promise<any[]> => {
        const pulled: any[] = [];
        let cursor = 0;
        let hasMoreLocal = true;
        while (hasMoreLocal) {
          const resLocal = await listClassmateQuizBattlesFromSupabase({
            q: q || undefined,
            scope: targetScope,
            limit: 100,
            offset: cursor,
            djangoUserId: targetScope === "mine" ? currentUserId : undefined,
          });
          if (!resLocal) break;
          pulled.push(...(resLocal.results ?? []));
          hasMoreLocal = Boolean(resLocal.has_more);
          cursor += 100;
          if (pulled.length >= 500) break;
        }
        return pulled;
      };

      const [publicRows, mineRows] = await Promise.all([pullRows("public"), pullRows("mine")]);
      const mergedById = new Map<number, any>();
      for (const row of [...publicRows, ...mineRows]) {
        if (favorites.has(Number(row.id))) {
          mergedById.set(Number(row.id), row);
        }
      }

      const ordered = normalizeQuizBattleFavoriteIds(params?.favoriteIds)
        .map((id) => mergedById.get(id))
        .filter((row): row is any => !!row);
      const sliced = ordered.slice(offset, offset + limit);
      return {
        results: sliced.map(mapRowToItem),
        count: ordered.length,
        limit,
        offset,
        has_more: offset + sliced.length < ordered.length,
      };
    }

    const res = await listClassmateQuizBattlesFromSupabase({
      q: q || undefined,
      scope,
      limit,
      offset,
      djangoUserId: scope === "mine" ? (currentUserId ?? undefined) : undefined,
    });

    if (!res) throw new Error("Failed to load quiz battles from Supabase");
    const results: QuizBattleSetItem[] = res.results.map(mapRowToItem);

    return {
      results,
      count: res.count,
      limit: res.limit,
      offset: res.offset,
      has_more: res.has_more,
    };
  }

  /** 상세/공유 링크: 비공개도 id만 있으면 조회 (anon 포함). */
  async getQuizBattleSet(setId: number): Promise<QuizBattleSetDetail> {
    const { getClassmateQuizBattleByLinkFromSupabase } = await import("./supabaseGames");
    const result = await getClassmateQuizBattleByLinkFromSupabase(setId);
    if (!result) throw new Error("Failed to load quiz battle from Supabase");
    return result as QuizBattleSetDetail;
  }

  /** 편집 화면: RLS(공개 또는 본인 소유)만. */
  async getQuizBattleSetForEdit(setId: number): Promise<QuizBattleSetDetail> {
    const { getClassmateQuizBattleFromSupabase } = await import("./supabaseGames");
    const result = await getClassmateQuizBattleFromSupabase(setId);
    if (!result) throw new Error("Failed to load quiz battle from Supabase");
    return result as QuizBattleSetDetail;
  }

  async createQuizBattleSet(data: {
    title: string;
    subject?: string;
    description?: string;
    is_public?: boolean;
    questions: QuizBattleQuestionPayload[];
  }): Promise<QuizBattleSetDetail> {
    const { createClassmateQuizBattleFromSupabase } = await import("./supabaseGames");
    const result = await createClassmateQuizBattleFromSupabase({
      title: data.title,
      subject: data.subject,
      description: data.description,
      is_public: data.is_public,
      questions_json: data.questions
    });
    if (!result) throw new Error("Failed to create quiz battle in Supabase");
    return result as QuizBattleSetDetail;
  }

  async updateQuizBattleSet(
    setId: number,
    data: {
      title: string;
      subject?: string;
      description?: string;
      is_public?: boolean;
      questions: QuizBattleQuestionPayload[];
    }
  ): Promise<QuizBattleSetDetail> {
    const { updateClassmateQuizBattleFromSupabase } = await import("./supabaseGames");
    const result = await updateClassmateQuizBattleFromSupabase(setId, {
      title: data.title,
      subject: data.subject,
      description: data.description,
      is_public: data.is_public,
      questions_json: data.questions,
    });
    if (!result) throw new Error("Failed to update quiz battle in Supabase");
    return result as QuizBattleSetDetail;
  }

  async deleteQuizBattleSet(setId: number): Promise<{ success: boolean }> {
    const { deleteClassmateQuizBattleFromSupabase } = await import("./supabaseGames");
    await deleteClassmateQuizBattleFromSupabase(setId);
    return { success: true };
  }

  async startQuizBattle(setId: number): Promise<QuizBattleStartResponse> {
    const { startClassmateQuizBattleFromSupabase } = await import("./supabaseGames");
    const result = await startClassmateQuizBattleFromSupabase(setId);
    if (!result) throw new Error("Failed to start quiz battle in Supabase");
    return result as QuizBattleStartResponse;
  }

  /**
   * 카드 매칭(구 카드뒤집기) 모드용 — 단답형 문항만 매칭 쌍으로 셔플.
   */
  async startQuizBattleCardFlip(
    setId: number,
    data?: { players?: number; pair_count?: number; include_bonus?: boolean }
  ): Promise<ClassmateGameStartResponse> {
    const { filterQuizBattleQuestionsByMode, buildQuizBattleCardFlipDeck } = await import("./quizBattleCore");
    const base = await this.startQuizBattle(setId);
    const { filtered, excludedCount } = filterQuizBattleQuestionsByMode(base.questions, "short_only");
    if (!filtered.length) {
      const hint =
        excludedCount > 0
          ? ` (객관식 ${excludedCount}개는 카드 매칭에 쓰이지 않습니다.)`
          : "";
      throw new Error(`카드 매칭에는 단답형 문항이 필요합니다.${hint}`);
    }
    const players = data?.players != null ? Math.max(1, Math.min(6, Math.floor(data.players))) : 2;
    const pair_count =
      data?.pair_count != null ? Math.max(3, Math.min(30, Math.floor(data.pair_count))) : undefined;
    const include_bonus = data?.include_bonus ?? true;
    const deckBase = buildQuizBattleCardFlipDeck(filtered, {
      pairCount: pair_count,
      includeBonus: include_bonus,
    });
    const nb = deckBase.filter((d) => d.face_type !== "bonus");
    const pairKeysCount = new Set(nb.map((d) => d.pair_key)).size;
    return {
      success: true,
      game: {
        id: setId,
        title: base.game.title,
        subject: base.game.subject ?? "",
        description: base.game.description ?? "",
        is_public: base.game.is_public,
      },
      config: {
        players,
        pair_count: pairKeysCount,
        include_bonus,
      },
      deck: deckBase,
    };
  }

  // Classmate: Classrooms
  async listClassmateClassrooms(): Promise<ClassmateClassroomsResponse> {
    const { listClassmateClassroomsFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateClassroomsFromSupabase();
    if (!sbResult) throw new Error("학급 목록을 불러오지 못했습니다.");
    return { results: sbResult.results as ClassmateClassroom[] };
  }

  async createClassmateClassroom(data: { name: string }): Promise<ClassmateClassroom> {
    const name = (data.name || "").trim();
    if (!name) throw new Error("학급명을 입력해주세요.");
    const { createClassmateClassroomFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateClassroomFromSupabase(name);
    if (!sbResult) throw new Error("학급을 생성하지 못했습니다. 로그인 상태를 확인해주세요.");
    return sbResult as ClassmateClassroom;
  }

  async getClassmateClassroom(id: number): Promise<ClassmateClassroom> {
    const { getClassmateClassroomFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await getClassmateClassroomFromSupabase(id);
    if (!sbResult) throw new Error("학급 정보를 불러오지 못했습니다.");
    return sbResult as ClassmateClassroom;
  }

  async updateClassmateClassroom(
    id: number,
    data: Partial<{ name: string; memo: string | null; activity_default_category_name: string; activity_default_category_sort_order: number; activity_default_category_icon: string }>
  ): Promise<{ success: boolean; classroom: ClassmateClassroom }> {
    if (
      data.name === undefined
      && data.memo === undefined
      && data.activity_default_category_name === undefined
      && data.activity_default_category_sort_order === undefined
      && data.activity_default_category_icon === undefined
    ) {
      throw new Error("변경할 학급 정보가 없습니다.");
    }
    const { updateClassmateClassroomFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await updateClassmateClassroomFromSupabase(id, data);
    if (sbResult) return { success: true, classroom: sbResult as ClassmateClassroom };
    if (data.activity_default_category_name !== undefined) {
      throw new Error("기본 평가 항목 이름을 변경하지 못했습니다.");
    }
    throw new Error(data.name !== undefined ? "학급 이름을 변경하지 못했습니다." : "학급 메모를 저장하지 못했습니다.");
  }

  async deleteClassmateClassroom(id: number): Promise<{ success: boolean }> {
    const { deleteClassmateClassroomFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await deleteClassmateClassroomFromSupabase(id);
    if (sbResult?.success) return sbResult;
    throw new Error("학급 삭제에 실패했습니다. 연결 데이터를 정리한 뒤 다시 시도해 주세요.");
  }

  // Classmate: Students
  async listClassmateStudents(classroomId: number): Promise<ClassmateStudentsResponse> {
    const { listClassmateStudentsFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateStudentsFromSupabase(classroomId);
    if (!sbResult) throw new Error("학생 목록을 불러오지 못했습니다.");
    const mappedResults = sbResult.results.map((row) => ({
      id: row.id,
      classroom: row.classroom_id,
      number: row.number,
      name: row.name,
      gender: row.gender,
      memo: row.memo,
      total_score: row.total_score,
      photo_storage_path: row.photo_storage_path ?? null,
    }));
    return { results: mappedResults };
  }

  async createClassmateStudent(
    classroomId: number,
    data: { number: number; name: string; gender: "M" | "F"; memo?: string | null }
  ): Promise<ClassmateStudent> {
    const { createClassmateStudentFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateStudentFromSupabase(classroomId, data);
    if (!sbResult) throw new Error("학생을 추가하지 못했습니다.");
    return {
      id: sbResult.id,
      classroom: sbResult.classroom_id,
      number: sbResult.number,
      name: sbResult.name,
      gender: sbResult.gender,
      memo: sbResult.memo,
      total_score: sbResult.total_score,
      photo_storage_path: sbResult.photo_storage_path ?? null,
    };
  }

  /** 엑셀 등 일괄 추가. Supabase 전용. */
  async createClassmateStudentsBulk(
    classroomId: number,
    rows: Array<{ number: number; name: string; gender: "M" | "F"; memo?: string | null }>
  ): Promise<{ inserted: number; errors?: Array<{ row: number; errors: Record<string, string[]> }> }> {
    if (!rows.length) return { inserted: 0 };
    const { bulkInsertClassmateStudents } = await import("./supabaseClassmate");
    const res = await bulkInsertClassmateStudents(classroomId, rows);
    if (!res) throw new Error("학생 일괄 등록에 실패했습니다.");
    return { inserted: res.inserted };
  }

  async updateClassmateStudent(
    studentId: number,
    data: Partial<{ number: number; name: string; gender: "M" | "F"; memo: string | null }>
  ): Promise<{ success: boolean; student: ClassmateStudent }> {
    const { updateClassmateStudentFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await updateClassmateStudentFromSupabase(studentId, data);
    if (!sbResult) throw new Error("학생 정보를 수정하지 못했습니다.");
    return {
      success: true,
      student: {
        id: sbResult.id,
        classroom: sbResult.classroom_id,
        number: sbResult.number,
        name: sbResult.name,
        gender: sbResult.gender,
        memo: sbResult.memo,
        total_score: sbResult.total_score,
        photo_storage_path: sbResult.photo_storage_path ?? null,
      },
    };
  }

  async uploadClassmateStudentPhoto(studentId: number, file: File): Promise<ClassmateStudent> {
    const { uploadClassmateStudentPhotoToSupabase } = await import("./supabaseClassmate");
    const row = await uploadClassmateStudentPhotoToSupabase(studentId, file);
    if (!row) throw new Error("사진 업로드에 실패했습니다.");
    return {
      id: row.id,
      classroom: row.classroom_id,
      number: row.number,
      name: row.name,
      gender: row.gender,
      memo: row.memo,
      total_score: row.total_score,
      photo_storage_path: row.photo_storage_path ?? null,
    };
  }

  async removeClassmateStudentPhoto(studentId: number): Promise<ClassmateStudent> {
    const { removeClassmateStudentPhotoFromSupabase } = await import("./supabaseClassmate");
    const row = await removeClassmateStudentPhotoFromSupabase(studentId);
    if (!row) throw new Error("사진 삭제에 실패했습니다.");
    return {
      id: row.id,
      classroom: row.classroom_id,
      number: row.number,
      name: row.name,
      gender: row.gender,
      memo: row.memo,
      total_score: row.total_score,
      photo_storage_path: row.photo_storage_path ?? null,
    };
  }

  async deleteClassmateStudent(studentId: number): Promise<{ success: boolean }> {
    const { deleteClassmateStudentFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await deleteClassmateStudentFromSupabase(studentId);
    if (!sbResult?.success) throw new Error("학생을 삭제하지 못했습니다.");
    return sbResult;
  }

  // Classmate: Activities
  async listAllClassmateActivities(_classroomIds?: number[]): Promise<{ results: (ClassmateActivityRecord & { student_name: string; student_number: number })[] }> {
    const { listAllClassmateActivitiesFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listAllClassmateActivitiesFromSupabase();
    if (!sbResult) throw new Error("활동 기록을 불러오지 못했습니다.");
    return { results: sbResult.results as (ClassmateActivityRecord & { student_name: string; student_number: number })[] };
  }

  async listClassmateClassroomActivities(classroomId: number): Promise<{ results: (ClassmateActivityRecord & { student_name: string; student_number: number })[] }> {
    const { listClassmateClassroomActivitiesFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateClassroomActivitiesFromSupabase(classroomId);
    if (!sbResult) throw new Error("학급 활동 기록을 불러오지 못했습니다.");
    return { results: sbResult.results as (ClassmateActivityRecord & { student_name: string; student_number: number })[] };
  }

  async listClassmateActivities(studentId: number): Promise<ClassmateActivitiesResponse> {
    const { listClassmateActivitiesFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateActivitiesFromSupabase(studentId);
    if (!sbResult) throw new Error("학생 활동 기록을 불러오지 못했습니다.");
    return { results: sbResult.results as ClassmateActivityRecord[] };
  }

  async listClassmateActivityCategories(classroomId: number): Promise<{ results: ClassmateActivityCategory[] }> {
    const { listClassmateActivityCategoriesFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateActivityCategoriesFromSupabase(classroomId);
    if (!sbResult) throw new Error("평가 항목을 불러오지 못했습니다.");
    return { results: sbResult.results as ClassmateActivityCategory[] };
  }

  async createClassmateActivityCategory(classroomId: number, name: string, icon?: string): Promise<ClassmateActivityCategory> {
    const { createClassmateActivityCategoryFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateActivityCategoryFromSupabase(classroomId, name, icon);
    if (!sbResult) throw new Error("평가 항목을 추가하지 못했습니다.");
    return sbResult as ClassmateActivityCategory;
  }

  async updateClassmateActivityCategory(
    categoryId: number,
    data: Partial<{ name: string; sort_order: number; icon: string }>
  ): Promise<ClassmateActivityCategory> {
    const { updateClassmateActivityCategoryFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await updateClassmateActivityCategoryFromSupabase(categoryId, data);
    if (!sbResult) throw new Error("평가 항목을 수정하지 못했습니다.");
    return sbResult as ClassmateActivityCategory;
  }

  async deleteClassmateActivityCategory(categoryId: number): Promise<{ success: boolean }> {
    const { deleteClassmateActivityCategoryFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await deleteClassmateActivityCategoryFromSupabase(categoryId);
    if (!sbResult?.success) throw new Error("평가 항목을 삭제하지 못했습니다.");
    return sbResult;
  }

  async createClassmateActivity(
    studentId: number,
    data: { content: string; score_change?: number; category?: number | null }
  ): Promise<ClassmateActivityRecord> {
    const { createClassmateActivityFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateActivityFromSupabase(studentId, data);
    if (!sbResult) throw new Error("활동 기록을 추가하지 못했습니다.");
    return sbResult as ClassmateActivityRecord;
  }

  async createClassmateActivitiesBulk(
    studentIds: number[],
    data: ClassmateActivityBulkEntry | ClassmateActivityBulkEntry[]
  ): Promise<{ inserted: number }> {
    const { createClassmateActivitiesBulkFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateActivitiesBulkFromSupabase(studentIds, data);
    if (!sbResult) throw new Error("활동 기록을 일괄 추가하지 못했습니다.");
    return sbResult;
  }

  async updateClassmateActivity(
    activityId: number,
    data: Partial<{ content: string; score_change: number; category: number | null }>
  ): Promise<{ success: boolean; activity: ClassmateActivityRecord }> {
    const { updateClassmateActivityFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await updateClassmateActivityFromSupabase(activityId, data);
    if (!sbResult) throw new Error("활동 기록을 수정하지 못했습니다.");
    return { success: true, activity: sbResult as ClassmateActivityRecord };
  }

  async deleteClassmateActivity(activityId: number): Promise<{ success: boolean }> {
    const { deleteClassmateActivityFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await deleteClassmateActivityFromSupabase(activityId);
    if (!sbResult?.success) throw new Error("활동 기록을 삭제하지 못했습니다.");
    return sbResult;
  }

  // Classmate: Random pick
  async classmateRandomPick(classroomId: number, count: number): Promise<ClassmateRandomPickResponse> {
    const { classmateRandomPickFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await classmateRandomPickFromSupabase(classroomId, count);
    if (!sbResult?.success) throw new Error("무작위 뽑기에 실패했습니다.");
    return sbResult as ClassmateRandomPickResponse;
  }

  // Classmate: Seat arrangements (v1)
  async listClassmateArrangements(classroomId?: number): Promise<{ results: ClassmateSeatArrangement[] }> {
    const { listClassmateArrangementsFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateArrangementsFromSupabase(classroomId);
    if (!sbResult) throw new Error("자리배치 목록을 불러오지 못했습니다.");
    return { results: sbResult.results as ClassmateSeatArrangement[] };
  }

  async createClassmateArrangement(data: {
    classroom: number;
    title: string;
    arrangement_type: "RC" | "GR";
    total_rows?: number | null;
    total_cols?: number | null;
    group_size?: number | null;
    num_groups?: number | null;
    gender_balance?: boolean;
    excluded_pairs?: string;
  }): Promise<ClassmateSeatArrangement> {
    const { createClassmateArrangementFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateArrangementFromSupabase(data);
    if (!sbResult) throw new Error("Failed to create layout in Supabase");
    return sbResult as ClassmateSeatArrangement;
  }

  async getClassmateArrangement(arrangementId: number): Promise<ClassmateArrangementDetail> {
    const { getClassmateArrangementFromSupabase, listClassmateStudentsFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await getClassmateArrangementFromSupabase(arrangementId);
    if (!sbResult) throw new Error("자리배치 정보를 불러오지 못했습니다.");

    let studentsObj: Record<number, any> = {};
    const studentsFetch = await listClassmateStudentsFromSupabase(sbResult.arrangement.classroom);
    if (studentsFetch) {
      studentsObj = studentsFetch.results.reduce((acc: Record<number, any>, student) => {
        acc[student.id] = {
          id: student.id,
          classroom: student.classroom_id,
          number: student.number,
          name: student.name,
          gender: student.gender,
          memo: student.memo,
          total_score: student.total_score,
          photo_storage_path: student.photo_storage_path ?? null,
        };
        return acc;
      }, {});
    }

    const seats = sbResult.seats.map((seat) => ({
      id: seat.id,
      arrangement: seat.arrangement,
      student: seat.student_id ? (studentsObj[seat.student_id] || { id: seat.student_id }) : null,
      row: seat.row,
      col: seat.col,
      group_number: seat.group_number,
      order_in_group: seat.order_in_group,
    }));

    return {
      ...sbResult.arrangement,
      seats,
    } as ClassmateArrangementDetail;
  }

  async updateClassmateArrangement(
    arrangementId: number,
    data: Partial<{
      title: string;
      gender_balance: boolean;
      excluded_pairs: string;
      randomize_strategy: ArrangementRandomizeStrategy;
      sociometry_survey_id: number | null;
      pinned_assignments: Record<string, number>;
    }>
  ): Promise<{ success: boolean; arrangement: ClassmateArrangementDetail }> {
    const { updateClassmateArrangementFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await updateClassmateArrangementFromSupabase(arrangementId, data);
    if (!sbResult) throw new Error("자리배치 정보를 수정하지 못했습니다.");
    return { success: true, arrangement: await this.getClassmateArrangement(arrangementId) };
  }

  async deleteClassmateArrangement(arrangementId: number): Promise<{ success: boolean }> {
    const { deleteClassmateArrangementFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await deleteClassmateArrangementFromSupabase(arrangementId);
    if (!sbResult?.success) throw new Error("자리배치를 삭제하지 못했습니다.");
    return sbResult;
  }

  async randomizeClassmateArrangement(arrangementId: number): Promise<{ success: boolean; arrangement: ClassmateArrangementDetail }> {
    const { randomizeClassmateArrangementFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await randomizeClassmateArrangementFromSupabase(arrangementId);
    if (!sbResult) throw new Error("자리배치 무작위 배정을 완료하지 못했습니다.");
    return { success: true, arrangement: await this.getClassmateArrangement(arrangementId) };
  }

  async clearClassmateArrangementSeats(arrangementId: number): Promise<{ success: boolean; arrangement: ClassmateArrangementDetail }> {
    const { clearClassmateArrangementSeatsFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await clearClassmateArrangementSeatsFromSupabase(arrangementId);
    if (!sbResult) throw new Error("배치 초기화에 실패했습니다.");
    return { success: true, arrangement: await this.getClassmateArrangement(arrangementId) };
  }

  async swapClassmateSeats(arrangementId: number, seat1_id: number, seat2_id: number): Promise<{ success: boolean }> {
    const { swapClassmateSeatsFromSupabase } = await import("./supabaseClassmate");
    const ok = await swapClassmateSeatsFromSupabase(arrangementId, seat1_id, seat2_id);
    if (ok) return { success: true };
    throw new Error("좌석 교환에 실패했습니다.");
  }

  // Classmate: Progress & Attendance (v1)
  async listClassmateProgress(): Promise<{ results: ClassmateProgress[] }> {
    const { listClassmateProgressFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateProgressFromSupabase();
    if (!sbResult) throw new Error("진도 목록을 불러오지 못했습니다.");
    return { results: sbResult.results as ClassmateProgress[] };
  }

  async createClassmateProgress(data: {
    subject: string;
    /** 칸 스냅샷·엔트리 subject fallback용. 생략 시 레거시처럼 subject를 fallback으로 사용 */
    default_entry_subject?: string | null;
    start_date: string;
    end_date: string;
    classrooms: number[];
    schedules: Array<{ classroom: number; day: number; period: number; subject?: string | null }>;
  }): Promise<ClassmateProgress> {
    const { createClassmateProgressFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateProgressFromSupabase(data);
    if (!sbResult) throw new Error("Failed to create progress in Supabase");
    return sbResult as ClassmateProgress;
  }

  async getClassmateProgress(progressId: number): Promise<ClassmateProgressDetail> {
    const { getClassmateProgressFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await getClassmateProgressFromSupabase(progressId);
    if (!sbResult) throw new Error("진도 정보를 불러오지 못했습니다.");
    // view 에는 classrooms id 배열만 있으므로 기존과 동일하게 임시 name 매핑 유지
    const classrooms = sbResult.classrooms.map((id: number) => ({ id, name: `Classroom ${id}` }));
    return { ...sbResult, classrooms } as ClassmateProgressDetail;
  }

  async deleteClassmateProgress(progressId: number): Promise<{ success: boolean }> {
    const { deleteClassmateProgressFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await deleteClassmateProgressFromSupabase(progressId);
    if (!sbResult?.success) throw new Error("진도 정보를 삭제하지 못했습니다.");
    return sbResult;
  }

  async toggleClassmateProgressFavorite(progressId: number): Promise<{ success: boolean; is_favorite: boolean }> {
    const { toggleClassmateProgressFavoriteFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await toggleClassmateProgressFavoriteFromSupabase(progressId);
    if (!sbResult?.success) throw new Error("즐겨찾기 상태를 변경하지 못했습니다.");
    return sbResult;
  }

  async listClassmateProgressEntries(
    progressId: number,
    params?: { classroomId?: number; start_date?: string; end_date?: string }
  ): Promise<{ results: ClassmateProgressEntry[] }> {
    try {
      const { listClassmateProgressEntriesFromSupabase } = await import("./supabaseClassmate");
      const sbResult = await listClassmateProgressEntriesFromSupabase(progressId, params);
      if (sbResult) return { results: sbResult.results as ClassmateProgressEntry[] };
    } catch (err) {
      console.error("Supabase load error", err);
    }
    return { results: [] };
  }

  async listClassmateTodayEntries(classroomId: number): Promise<{ date: string; results: ClassmateTodayEntry[] }> {
    const { listClassmateTodayEntriesFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateTodayEntriesFromSupabase(classroomId);
    if (!sbResult) throw new Error("오늘 진도 항목을 불러오지 못했습니다.");
    return { date: sbResult.date, results: sbResult.results as ClassmateTodayEntry[] };
  }

  async createClassmateProgressEntry(
    progressId: number,
    data: { classroom?: number | null; date: string; period: number; subject?: string | null; pages_completed?: number; notes?: string | null; display_color?: string | null }
  ): Promise<ClassmateProgressEntry> {
    const { createClassmateProgressEntryFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateProgressEntryFromSupabase(progressId, data);
    if (!sbResult) throw new Error("진도 항목을 추가하지 못했습니다.");
    return sbResult as ClassmateProgressEntry;
  }

  async updateClassmateProgressEntry(entryId: number, data: Partial<Omit<ClassmateProgressEntry, "id" | "class_progress">>): Promise<{ success: boolean; entry: ClassmateProgressEntry }> {
    const { updateClassmateProgressEntryFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await updateClassmateProgressEntryFromSupabase(entryId, data);
    if (!sbResult) throw new Error("Failed to update entry in Supabase");
    return { success: true, entry: sbResult as ClassmateProgressEntry };
  }

  async deleteClassmateProgressEntry(entryId: number): Promise<{ success: boolean }> {
    const { deleteClassmateProgressEntryFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await deleteClassmateProgressEntryFromSupabase(entryId);
    if (!sbResult || !sbResult.success) throw new Error("Failed to delete entry in Supabase");
    return sbResult;
  }

  async listClassmateProgressDayMemos(
    progressId: number,
    params?: { start_date?: string; end_date?: string }
  ): Promise<{ results: ClassmateProgressDayMemo[] }> {
    const { listClassmateProgressDayMemosFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateProgressDayMemosFromSupabase(progressId, params);
    if (!sbResult) return { results: [] };
    return { results: sbResult.results as ClassmateProgressDayMemo[] };
  }

  async upsertClassmateProgressDayMemo(
    progressId: number,
    data: { date: string; memo: string | null }
  ): Promise<ClassmateProgressDayMemo> {
    const { upsertClassmateProgressDayMemoFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await upsertClassmateProgressDayMemoFromSupabase(progressId, data.date, data.memo);
    if (!sbResult) throw new Error("날짜 메모를 저장하지 못했습니다.");
    return sbResult as ClassmateProgressDayMemo;
  }

  async getClassmateAttendance(entryId: number): Promise<{ students?: any[], results: ClassmateAttendanceRow[] }> {
    const { getClassmateAttendanceFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await getClassmateAttendanceFromSupabase(entryId);
    if (!sbResult) throw new Error("Failed to load attendance from Supabase");
    return { students: sbResult.students as any[], results: sbResult.results as ClassmateAttendanceRow[] };
  }

  async saveClassmateAttendance(entryId: number, data: { rows: Array<{ student: number; status: ClassmateAttendanceRow["status"]; reason?: ClassmateAttendanceRow["reason"]; note?: string | null }> }): Promise<{ success: boolean; results: ClassmateAttendanceRow[] }> {
    const { saveClassmateAttendanceFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await saveClassmateAttendanceFromSupabase(entryId, data);
    if (!sbResult) throw new Error("Failed to save attendance in Supabase");
    return { success: true, results: sbResult.results as ClassmateAttendanceRow[] };
  }

  // Classmate: Votes (v1)
  async listClassmateVotes(classroomId?: number): Promise<{ results: ClassmateVote[] }> {
    const { listClassmateVotesFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await listClassmateVotesFromSupabase(classroomId);
    if (!sbResult) throw new Error("투표 목록을 불러오지 못했습니다.");
    // classmate_votes_view에는 candidate 상세가 없어 기존처럼 id만 매핑 유지
    const results = sbResult.results.map((v) => ({
      ...v,
      candidates: v.candidate_ids.map((id) => ({ id, number: 0, name: "" })),
    })) as unknown as ClassmateVote[];
    return { results };
  }

  async createClassmateVote(data: { classroom: number; title: string; candidate_ids: number[]; cooldown_seconds?: number; is_active?: boolean }): Promise<ClassmateVote> {
    const { createClassmateVoteFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await createClassmateVoteFromSupabase(data);
    if (!sbResult) throw new Error("투표를 생성하지 못했습니다.");
    return {
      ...sbResult,
      candidates: sbResult.candidate_ids.map((id: number) => ({ id, number: 0, name: "" })),
    } as unknown as ClassmateVote;
  }

  async getClassmateVote(voteId: number): Promise<ClassmateVote> {
    const { getClassmateVoteFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await getClassmateVoteFromSupabase(voteId);
    if (!sbResult) throw new Error("투표 정보를 불러오지 못했습니다.");
    const vote = {
      ...sbResult,
      candidates: sbResult.candidate_ids.map((id) => ({ id, number: 0, name: "" })),
    } as unknown as ClassmateVote;
    return vote;
  }

  async castClassmateVote(voteId: number, data: { candidate_id: number }): Promise<{ success: boolean; total_votes?: number }> {
    const { castClassmateVoteFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await castClassmateVoteFromSupabase(voteId, data);
    if (!sbResult?.success) throw new Error("투표에 실패했습니다.");
    return sbResult;
  }

  async getClassmateVoteResult(voteId: number): Promise<ClassmateVoteResult> {
    const { getClassmateVoteResultFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await getClassmateVoteResultFromSupabase(voteId);
    if (!sbResult) throw new Error("투표 결과를 불러오지 못했습니다.");
    // 후보 상세는 view에 없어 id만 매핑 유지
    const results = sbResult.results.map((r) => ({
      vote_count: r.vote_count,
      candidate: { id: r.candidate, number: 0, name: "" },
    }));
    return { vote: voteId, results } as unknown as ClassmateVoteResult;
  }

  async deleteClassmateVote(voteId: number): Promise<{ success: boolean }> {
    const { deleteClassmateVoteFromSupabase } = await import("./supabaseClassmate");
    const sbResult = await deleteClassmateVoteFromSupabase(voteId);
    if (!sbResult?.success) throw new Error("투표를 삭제하지 못했습니다.");
    return sbResult;
  }

  // Classmate Attendance Management
  async getClassmateAttendanceDashboard(): Promise<{ results: any[] }> {
    const { classmateAttendanceDashboardFromSupabase } = await import('./supabaseClassmate');
    const supabaseData = await classmateAttendanceDashboardFromSupabase();
    if (!supabaseData) throw new Error("Failed to load attendance dashboard from Supabase");
    return supabaseData;
  }

  async getClassmateAttendanceDetail(classroomId: number, params?: { subject?: string; start_date?: string; end_date?: string }): Promise<any> {
    const { classmateAttendanceDetailFromSupabase } = await import('./supabaseClassmate');
    const supabaseData = await classmateAttendanceDetailFromSupabase(classroomId, params);
    if (!supabaseData) throw new Error("Failed to load attendance details from Supabase");
    return supabaseData;
  }

  async listClassmateAttendanceRegisters(classroomId?: number): Promise<{ results: ClassmateAttendanceRegister[] }> {
    const { listClassmateAttendanceRegistersFromSupabase } = await import("./supabaseClassmate");
    const sb = await listClassmateAttendanceRegistersFromSupabase(classroomId);
    if (!sb) throw new Error("출석부 목록을 불러오지 못했습니다.");
    return sb;
  }

  async getClassmateAttendanceRegister(registerId: number): Promise<ClassmateAttendanceRegister> {
    const { getClassmateAttendanceRegisterFromSupabase } = await import("./supabaseClassmate");
    const sb = await getClassmateAttendanceRegisterFromSupabase(registerId);
    if (!sb) throw new Error("출석부를 불러오지 못했습니다.");
    return sb;
  }

  async createClassmateAttendanceRegister(data: {
    classroom_id: number;
    name: string;
    start_date: string;
    end_date: string;
  }): Promise<ClassmateAttendanceRegister> {
    const { createClassmateAttendanceRegisterFromSupabase } = await import("./supabaseClassmate");
    const sb = await createClassmateAttendanceRegisterFromSupabase(data);
    if (!sb) throw new Error("출석부를 생성하지 못했습니다.");
    return sb;
  }

  async updateClassmateAttendanceRegister(
    registerId: number,
    data: { name?: string; start_date?: string; end_date?: string }
  ): Promise<ClassmateAttendanceRegister> {
    const { updateClassmateAttendanceRegisterFromSupabase } = await import("./supabaseClassmate");
    const sb = await updateClassmateAttendanceRegisterFromSupabase(registerId, data);
    if (!sb) throw new Error("출석부를 수정하지 못했습니다.");
    return sb;
  }

  async deleteClassmateAttendanceRegister(registerId: number): Promise<{ success: boolean }> {
    const { deleteClassmateAttendanceRegisterFromSupabase } = await import("./supabaseClassmate");
    const sb = await deleteClassmateAttendanceRegisterFromSupabase(registerId);
    if (!sb.success) throw new Error("출석부를 삭제하지 못했습니다.");
    return sb;
  }

  async getClassmateDailyAttendance(registerId: number, date: string): Promise<{
    register: ClassmateAttendanceRegister;
    classroom: { id: number; name: string };
    date: string;
    students: Array<{ id: number; number: number; name: string; gender: string }>;
    rows: ClassmateDailyAttendanceMergedRow[];
  }> {
    const { getClassmateDailyAttendanceFromSupabase } = await import("./supabaseClassmate");
    const sb = await getClassmateDailyAttendanceFromSupabase(registerId, date);
    if (!sb) throw new Error("일일 출석을 불러오지 못했습니다.");
    return sb;
  }

  async saveClassmateDailyAttendance(
    registerId: number,
    date: string,
    data: {
      rows: Array<{
        student_id: number;
        status: ClassmateAttendanceRow["status"];
        reason?: ClassmateAttendanceRow["reason"];
        note?: string | null;
      }>;
    }
  ): Promise<{
    register: ClassmateAttendanceRegister;
    classroom: { id: number; name: string };
    date: string;
    students: Array<{ id: number; number: number; name: string; gender: string }>;
    rows: ClassmateDailyAttendanceMergedRow[];
  }> {
    const { saveClassmateDailyAttendanceFromSupabase } = await import("./supabaseClassmate");
    const sb = await saveClassmateDailyAttendanceFromSupabase(registerId, date, data);
    if (!sb) throw new Error("일일 출석을 저장하지 못했습니다.");
    return sb;
  }

  async getClassmateRegisterStats(
    registerId: number,
    params?: { start_date?: string; end_date?: string }
  ): Promise<any> {
    const { getClassmateRegisterStatsFromSupabase } = await import("./supabaseClassmate");
    const sb = await getClassmateRegisterStatsFromSupabase(registerId, params);
    if (!sb) throw new Error("출석부 통계를 불러오지 못했습니다.");
    return sb;
  }

  logout() {
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } catch {
      // localStorage 접근 실패 시 무시
    }
    window.location.href = '/';
  }

  // School APIs (Vercel Edge Functions - same-origin)
  async getSchoolInfo(): Promise<any> {
    const { supabase: sb } = await import('./supabase');
    const supabaseToken = sb ? (await sb.auth.getSession()).data.session?.access_token : null;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const res = await fetch(`${origin}/api/school/info`, {
      method: 'GET',
      headers: supabaseToken ? { 'Authorization': `Bearer ${supabaseToken}` } : {},
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getSchoolDisclosure(atptCode: string, sdCode: string, signal?: AbortSignal): Promise<import('./schoolOverview').SchoolDisclosure> {
    const params = new URLSearchParams({ atpt_ofcdc_sc_code: atptCode, sd_schul_code: sdCode });
    const res = await fetch(`/api/school/disclosure?${params}`, { signal });
    if (!res.ok) throw new Error('학교 공시정보를 불러오지 못했습니다.');
    const data = await res.json();
    if (!Array.isArray(data?.sections)) throw new Error('학교 공시정보를 확인할 수 없습니다.');
    return data;
  }

  async getSchoolClasses(atptCode: string, sdCode: string, signal?: AbortSignal): Promise<import('./schoolOverview').SchoolClasses> {
    const params = new URLSearchParams({ atpt_ofcdc_sc_code: atptCode, sd_schul_code: sdCode });
    const res = await fetch(`/api/school/classes?${params}`, { signal });
    if (!res.ok) throw new Error('학급 정보를 불러오지 못했습니다.');
    const data = await res.json();
    if (!Array.isArray(data?.groups)) throw new Error('학급 정보를 확인할 수 없습니다.');
    return data;
  }

  async getSchoolMeal(date?: string, atptCode?: string, sdCode?: string): Promise<{ mealTypes: string[]; meals: Record<string, string[]>; date: string }> {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const queryDate = date || formatYmdInSeoul();
    const params = new URLSearchParams();
    params.append('date', queryDate);
    if (atptCode) params.append('atpt_ofcdc_sc_code', atptCode);
    if (sdCode) params.append('sd_schul_code', sdCode);
    const res = await fetch(`${origin}/api/school/meal?${params.toString()}`, { method: 'GET' });
    if (res.status === 404) {
      return { mealTypes: [], meals: {}, date: queryDate };
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getSchoolSchedule(start: string, end: string, atptCode?: string, sdCode?: string): Promise<Array<{ title: string; start: string }>> {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const params = new URLSearchParams();
    params.append('start', start);
    params.append('end', end);
    if (atptCode) params.append('atpt_ofcdc_sc_code', atptCode);
    if (sdCode) params.append('sd_schul_code', sdCode);
    const res = await fetch(`${origin}/api/school/schedule?${params.toString()}`, { method: 'GET' });
    if (!res.ok) return [];
    return res.json();
  }

  async searchSchool(query: string): Promise<{
    schools: Array<{
      id: number;
      ATPT_OFCDC_SC_NM: string;
      SCHUL_NM: string;
      source?: "db" | "neis";
      school_candidate?: {
        ATPT_OFCDC_SC_CODE: string;
        SD_SCHUL_CODE: string;
      };
    }>;
  }> {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const q = (query || "").trim();
    if (!q) return { schools: [] };
    const res = await fetch(`${origin}/api/school/search?q=${encodeURIComponent(q)}`, { method: "GET" });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || "학교 검색에 실패했습니다.");
    }
    return res.json() as Promise<{
      schools: Array<{
        id: number;
        ATPT_OFCDC_SC_NM: string;
        SCHUL_NM: string;
        source?: "db" | "neis";
        school_candidate?: {
          ATPT_OFCDC_SC_CODE: string;
          SD_SCHUL_CODE: string;
        };
      }>;
    }>;
  }

  /** school_schoolinfo 단건 (NEIS 코드·주소 등) — Vercel /api/school/detail */
  async getSchoolDetailById(id: number): Promise<Record<string, unknown>> {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const res = await fetch(`${origin}/api/school/detail?id=${encodeURIComponent(String(id))}`, { method: "GET" });
    if (res.status === 404) {
      throw new Error("학교를 찾을 수 없습니다.");
    }
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || "학교 정보를 불러오지 못했습니다.");
    }
    return res.json() as Promise<Record<string, unknown>>;
  }

  /**
   * NEIS 시간표 (학교 id + 날짜). 로그인 불필요 — 온라인 교무실 공개 페이지용.
   * 기존 getSchoolTimetable(Django)와 별도.
   */
  async getNeisClassTimetableBySchoolId(schoolId: number, date?: string): Promise<NeisClassTimetableResponse> {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const params = new URLSearchParams();
    params.append("id", String(schoolId));
    if (date) params.append("date", date);
    const res = await fetch(`${origin}/api/school/class-timetable?${params.toString()}`, { method: "GET" });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || "시간표를 불러오지 못했습니다.");
    }
    return res.json() as Promise<NeisClassTimetableResponse>;
  }

  /** 우리학교 시간표: 단일 날짜 (생략 시 오늘 또는 가장 가까운 미래 수업일) — Vercel /api/school/timetable (Django 미사용) */
  async getSchoolTimetable(date?: string): Promise<SchoolTimetableResponse> {
    const { supabase: sb } = await import("./supabase");
    const supabaseToken = sb ? (await sb.auth.getSession()).data.session?.access_token : null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const q = date ? `?date=${encodeURIComponent(date)}` : "";
    const res = await fetch(`${origin}/api/school/timetable${q}`, {
      method: "GET",
      headers: supabaseToken ? { Authorization: `Bearer ${supabaseToken}` } : {},
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      let msg = t || "시간표를 불러오지 못했습니다.";
      try {
        const j = JSON.parse(t);
        msg = j.error || j.detail || msg;
      } catch {
        /* use msg */
      }
      throw new Error(msg);
    }
    return res.json() as Promise<SchoolTimetableResponse>;
  }

  /** 우리학교 시간표: 기간 + 학년별 — Vercel /api/school/timetable POST */
  async getSchoolTimetableRange(options: {
    grades?: string[];
    start_date: string;
    end_date?: string;
  }): Promise<SchoolTimetableResponse> {
    const { supabase: sb } = await import("./supabase");
    const supabaseToken = sb ? (await sb.auth.getSession()).data.session?.access_token : null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (supabaseToken) headers.Authorization = `Bearer ${supabaseToken}`;
    const res = await fetch(`${origin}/api/school/timetable`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        grades: options.grades || ["1", "2", "3"],
        start_date: options.start_date,
        end_date: options.end_date ?? options.start_date,
      }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      let msg = t || "시간표를 불러오지 못했습니다.";
      try {
        const j = JSON.parse(t);
        msg = j.error || j.detail || msg;
      } catch {
        /* use msg */
      }
      throw new Error(msg);
    }
    return res.json() as Promise<SchoolTimetableResponse>;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Timetable Manager API
  // ═══════════════════════════════════════════════════════════════════════

  private tt(path: string) { return `/api/timetable${path}`; }

  async ttSchoolList(): Promise<TTSchool[]> {
    const s = await getSchoolsWithRoleFromSupabase();
    if (s) return s;
    throw new Error("Supabase school list failed");
  }
  async ttSchoolCreate(data: { name: string; total_grades: number }): Promise<TTSchool> {
    const currentUser = JSON.parse(localStorage.getItem('user_data') || '{}');
    if (!currentUser?.id) throw new Error("로그인이 필요합니다.");
    const s = await createSchoolInSupabase(data.name, data.total_grades, currentUser.id);
    if (s) return s;
    throw new Error("Supabase school create failed");
  }
  async ttSchoolDetail(id: number): Promise<TTSchool> {
    const s = await getSchoolDetailFromSupabase(id);
    if (s) return s;
    throw new Error("학교 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
  }
  async ttSchoolUpdate(id: number, data: Partial<{ name: string; total_grades: number }>): Promise<TTSchool> {
    const s = await updateSchoolInSupabase(id, data);
    if (s) return s;
    throw new Error("Supabase school update failed");
  }
  async ttSchoolDelete(id: number): Promise<void> {
    const s = await deleteSchoolInSupabase(id);
    if (s) return;
    throw new Error("Supabase school delete failed");
  }

  // --- Excel Import ---
  async importTimetableExcel(schoolId: number, file: File, sheetName?: string, options: import("./timetableExcelImportValidate").TimetableImportOptions = {}): Promise<ImportTimetableResult> {
    return importTimetableFromExcel(schoolId, file, sheetName, options);
  }

  async ttSchoolSummary(id: number): Promise<TTSchoolSummary> {
    const s = await getSchoolSummaryFromSupabase(id);
    if (s) return s as unknown as TTSchoolSummary;
    throw new Error("Supabase school summary failed");
  }

  async ttGradeList(schoolId: number): Promise<TTGradeInfo[]> {
    const s = await getGradesFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase grade list failed");
  }
  async ttGradeCreate(schoolId: number, data: Omit<TTGradeInfo, 'id' | 'total_periods'>): Promise<TTGradeInfo> {
    const s = await createGradeInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase grade create failed");
  }
  async ttGradeUpdate(schoolId: number, gradeId: number, data: Partial<TTGradeInfo>): Promise<TTGradeInfo> {
    const s = await updateGradeInSupabase(gradeId, data);
    if (s) return s;
    throw new Error("Supabase grade update failed");
  }
  async ttGradeDelete(schoolId: number, gradeId: number): Promise<void> {
    throwUnlessTimetableDeleteOk(
      await deleteGradeInSupabase(gradeId),
      "Supabase grade delete failed"
    );
  }

  // Places
  async ttPlaceList(schoolId: number): Promise<TTPlace[]> {
    const s = await getPlacesFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase place list failed");
  }
  async ttPlaceCreate(schoolId: number, data: Omit<TTPlace, 'id'>): Promise<TTPlace> {
    const s = await createPlaceInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase place create failed");
  }
  async ttPlaceUpdate(schoolId: number, placeId: number, data: Partial<TTPlace>): Promise<TTPlace> {
    const s = await updatePlaceInSupabase(placeId, data);
    if (s) return s;
    throw new Error("Supabase place update failed");
  }
  async ttPlaceDelete(schoolId: number, placeId: number): Promise<void> {
    throwUnlessTimetableDeleteOk(
      await deletePlaceInSupabase(placeId),
      "Supabase place delete failed"
    );
  }

  // Classrooms
  async ttClassroomList(schoolId: number): Promise<TTClassroom[]> {
    const s = await getClassroomsFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase classroom list failed");
  }
  async ttClassroomDelete(schoolId: number, classroomId: number): Promise<void> {
    throwUnlessTimetableDeleteOk(
      await deleteClassroomInSupabase(classroomId),
      "Supabase classroom delete failed"
    );
  }

  // Timeslots
  async ttTimeslotList(schoolId: number): Promise<{ id: number; grade: number; day: string; period: number; start_time: string; end_time: string }[]> {
    const s = await getTimeslotsFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase timeslot list failed");
  }
  async ttTimeslotBulkCreate(schoolId: number, grade: number, slots: { day: string; period: number; start_time: string; end_time: string }[]): Promise<{ id: number; grade: number; day: string; period: number; start_time: string; end_time: string }[]> {
    const s = await bulkCreateTimeslotsInSupabase(schoolId, grade, slots);
    if (s) return s;
    throw new Error("Supabase timeslot bulk create failed");
  }

  // Subjects
  async ttSubjectList(schoolId: number): Promise<TTSubject[]> {
    const s = await getSubjectsFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase subject list failed");
  }
  async ttSubjectCreate(schoolId: number, data: { name: string; default_place?: number | null }): Promise<TTSubject> {
    const s = await createSubjectInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase subject create failed");
  }
  async ttSubjectBulkCreate(schoolId: number, names: string): Promise<TTSubject[]> {
    const s = await bulkCreateSubjectsInSupabase(schoolId, names);
    if (s) return s;
    throw new Error("Supabase subject bulk create failed");
  }
  async ttSubjectUpdate(_schoolId: number, subjectId: number, data: Partial<TTSubject>): Promise<TTSubject> {
    const s = await updateSubjectInSupabase(subjectId, data);
    if (s) return s;
    throw new Error("Supabase subject update failed");
  }
  async ttSubjectDelete(_schoolId: number, subjectId: number): Promise<void> {
    throwUnlessTimetableDeleteOk(
      await deleteSubjectInSupabase(subjectId),
      "Supabase subject delete failed"
    );
  }

  // Teachers
  async ttTeacherList(schoolId: number): Promise<TTTeacher[]> {
    const s = await getTeachersFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase teacher list failed");
  }
  async ttTeacherCreate(schoolId: number, data: { name: string; homeroom?: number | null; subjects?: number[]; unavailable_times?: { day: string; period: number }[] }): Promise<TTTeacherDetail> {
    const s = await createTeacherInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase teacher create failed");
  }
  async ttTeacherDetail(schoolId: number, teacherId: number): Promise<TTTeacherDetail> {
    const s = await getTeacherDetailFromSupabase(teacherId);
    if (s) return s;
    throw new Error("Supabase teacher detail failed");
  }
  async ttTeacherUpdate(schoolId: number, teacherId: number, data: Partial<{ name: string; homeroom: number | null; subjects: number[]; unavailable_times: { day: string; period: number }[] }>): Promise<TTTeacherDetail> {
    const s = await updateTeacherInSupabase(schoolId, teacherId, data);
    if (s) return s;
    throw new Error("Supabase teacher update failed");
  }
  async ttTeacherDelete(_schoolId: number, teacherId: number): Promise<void> {
    throwUnlessTimetableDeleteOk(
      await deleteTeacherInSupabase(teacherId),
      "Supabase teacher delete failed"
    );
  }

  // Teaching Assignments
  async ttAssignmentList(schoolId: number): Promise<TTTeachingAssignment[]> {
    const s = await getAssignmentsFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase assignment list failed");
  }
  async ttAssignmentCreate(schoolId: number, data: { teacher: number; subject: number; classrooms: number[]; hours_per_week: number; is_block?: boolean; block_size?: number; single_hours?: number; place?: number | null }): Promise<TTTeachingAssignment[]> {
    const s = await createAssignmentInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase assignment create failed");
  }
  async ttAssignmentUpdate(_schoolId: number, assignmentId: number, data: Partial<TTTeachingAssignment>): Promise<TTTeachingAssignment> {
    const s = await updateAssignmentInSupabase(assignmentId, data);
    if (s) return s;
    throw new Error("Supabase assignment update failed");
  }
  async ttAssignmentDelete(_schoolId: number, assignmentId: number): Promise<void> {
    throwUnlessTimetableDeleteOk(
      await deleteAssignmentInSupabase(assignmentId),
      "Supabase assignment delete failed"
    );
  }
  async ttAssignmentSummary(schoolId: number): Promise<TTAssignmentSummaryItem[]> {
    const s = await getAssignmentSummaryFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase assignment summary failed");
  }

  // Fixed Schedules
  async ttFixedScheduleList(schoolId: number): Promise<TTSchedule[]> {
    const s = await getFixedSchedulesFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase fixed schedule list failed");
  }
  async ttFixedScheduleCreate(schoolId: number, data: Partial<TTSchedule>): Promise<TTSchedule> {
    const s = await createFixedScheduleInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase fixed schedule create failed");
  }
  async ttFixedScheduleUpdate(schoolId: number, scheduleId: number, data: Partial<TTSchedule>): Promise<TTSchedule> {
    const s = await updateFixedScheduleInSupabase(schoolId, scheduleId, data);
    if (s) return s;
    throw new Error("Supabase fixed schedule update failed");
  }
  async ttFixedScheduleDelete(schoolId: number, scheduleId: number): Promise<void> {
    const s = await deleteScheduleInSupabase(scheduleId);
    if (!s) throw new Error("Supabase delete failed (check RLS policies)");
  }

  // Team Teaching (팀 티칭)
  async ttTeamTeachingList(schoolId: number): Promise<TTTeamTeachingGroup[]> {
    const s = await getTeamTeachingGroupsFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase team teaching list failed");
  }
  async ttTeamTeachingCreate(schoolId: number, data: { subject: number; hours_per_week: number; teachers: number[]; classrooms: number[]; is_fixed?: boolean; day?: string | null; period?: number | null }): Promise<TTTeamTeachingGroup> {
    const s = await createTeamTeachingGroupInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase team teaching create failed");
  }
  async ttTeamTeachingDetail(_schoolId: number, groupId: number): Promise<TTTeamTeachingGroup> {
    const s = await getTeamTeachingGroupDetailFromSupabase(groupId);
    if (s) return s;
    throw new Error("Supabase team teaching detail failed");
  }
  async ttTeamTeachingUpdate(schoolId: number, groupId: number, data: Partial<{ subject: number; hours_per_week: number; teachers: number[]; classrooms: number[]; is_fixed: boolean; day: string | null; period: number | null }>): Promise<TTTeamTeachingGroup> {
    const s = await updateTeamTeachingGroupInSupabase(groupId, data);
    if (s) return s;
    throw new Error("Supabase team teaching update failed");
  }
  async ttFixedScheduleHomeroomBulk(schoolId: number, data: { day: string; period: number; subject_name?: string }): Promise<{ created: TTSchedule[]; created_count: number; skipped: string[] }> {
    const s = await bulkCreateHomeroomFixedSchedulesInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase homeroom bulk create failed");
  }
  async ttTeamTeachingDelete(schoolId: number, groupId: number): Promise<void> {
    throwUnlessTimetableDeleteOk(
      await deleteTeamTeachingGroupInSupabase(groupId),
      "Supabase team teaching delete failed"
    );
  }

  // Constraints
  async ttConstraintGet(schoolId: number): Promise<TTConstraintConfig> {
    const s = await getConstraintConfigFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase constraint get failed");
  }
  async ttConstraintUpdate(schoolId: number, data: Partial<TTConstraintConfig>): Promise<TTConstraintConfig> {
    const s = await updateConstraintConfigInSupabase(schoolId, data);
    if (s) return s;
    throw new Error("Supabase constraint update failed");
  }

  // Timetables
  async ttTimetableList(schoolId: number): Promise<TTTimetable[]> {
    const s = await getTimetablesFromSupabase(schoolId);
    if (s) return s;
    throw new Error("Supabase timetable list failed");
  }
  async ttTimetableDetail(schoolId: number, timetableId: number): Promise<TTTimetableDetail> {
    const s_schedules = await getTimetableSchedulesFromSupabase(schoolId, timetableId);
    if (s_schedules) {
      const t_info = await getTimetablesFromSupabase(schoolId).then(res => res?.find(x => x.id === timetableId));
      if (t_info) {
        // Map Supabase RPC field names to TTSchedule expected fields
        const mappedSchedules = (s_schedules as unknown as Record<string, unknown>[]).map(s => ({
          ...s,
          classroom_display: s.classroom_name || (s.classroom_grade && s.classroom_class_num ? `${s.classroom_grade}-${s.classroom_class_num}` : null),
          schedule_type_display: s.schedule_type === 'REG' ? '정규수업' : s.schedule_type === 'FIX' ? '고정수업' : s.schedule_type === 'HOM' ? '담임시간' : '특별수업',
        }));
        return {
          ...t_info,
          schedules: mappedSchedules
        } as unknown as TTTimetableDetail;
      }
    }
    throw new Error("Supabase timetable detail failed");
  }
  async ttTimetableDelete(schoolId: number, timetableId: number): Promise<void> {
    throwUnlessTimetableDeleteOk(
      await deleteTimetableInSupabase(timetableId),
      "Supabase timetable delete failed"
    );
  }
  async ttTimetableActivate(schoolId: number, timetableId: number): Promise<TTTimetable> {
    const s = await activateTimetableInSupabase(schoolId, timetableId);
    if (s) return s;
    throw new Error("Supabase timetable activate failed");
  }
  async ttTimetableDeactivate(schoolId: number, timetableId: number): Promise<TTTimetable> {
    const s = await deactivateTimetableInSupabase(schoolId, timetableId);
    if (s) return s;
    throw new Error("Supabase timetable deactivate failed");
  }
  async ttTimetableCopy(schoolId: number, timetableId: number, name?: string): Promise<TTTimetable> {
    const s = await copyTimetableInSupabase(schoolId, timetableId, name);
    if (s) return s;
    throw new Error("Supabase timetable copy failed");
  }
  async ttTimetableImportExcel(schoolId: number, file: File, sheetName?: string): Promise<ImportTimetableResult> {
    return importTimetableFromExcel(schoolId, file, sheetName);
  }

  // Generation
  async ttGenerate(schoolId: number, name?: string): Promise<TTGenerateStartResponse> {
    const payload: TTGenerateStartRequest = { name };
    const endpoint = this.tt(`/schools/${schoolId}/timetables/generate/`);
    const raw = await this.workmateRequest<unknown>(endpoint, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return parseGenerateStartResponse(raw);
  }
  async ttGenerateStatus(schoolId: number, taskId: string): Promise<TTGenerateStatus> {
    const endpoint = this.tt(`/schools/${schoolId}/timetables/generate-status/`) + `?taskId=${encodeURIComponent(taskId)}`;
    const raw = await this.workmateRequest<unknown>(endpoint);
    return parseGenerateStatusResponse(raw);
  }

  // Timetable Editing
  async ttMoveSchedule(schoolId: number, timetableId: number, data: { schedule_id: number; to_day: string; to_period: number; swap_with?: number | null; override_fixed?: boolean }): Promise<TTSchedule[]> {
    const flattenErr = (r: any) => {
      if (Array.isArray(r?.errors) && r.errors.length > 0) {
        return r.errors.map((e: any) => e?.message || e?.code || '오류').filter(Boolean).join('\n');
      }
      return r?.error || '요청에 실패했습니다.';
    };
    if (data.swap_with) {
      const r = await swapSchedulesInSupabase(schoolId, data.schedule_id, data.swap_with, { overrideFixed: data.override_fixed });
      if (r) {
        if (!r.success) throw { response: { data: { error: flattenErr(r), errors: r.errors } } };
        return [] as TTSchedule[]; // caller reloads anyway
      }
    } else {
      const r = await moveScheduleInSupabase(schoolId, data.schedule_id, data.to_day, data.to_period, { overrideFixed: data.override_fixed });
      if (r) {
        if (!r.success) throw { response: { data: { error: flattenErr(r), errors: r.errors } } };
        return [] as TTSchedule[];
      }
    }
    throw new Error("Supabase move/swap failed");
  }
  async ttToggleFixed(schoolId: number, timetableId: number, scheduleId: number): Promise<TTSchedule> {
    const r = await toggleScheduleFixedInSupabase(schoolId, scheduleId);
    if (r) {
      if (!r.success) throw { response: { data: { error: r.error } } };
      return { id: scheduleId, is_fixed: r.is_fixed } as unknown as TTSchedule;
    }
    throw new Error("Supabase toggle fixed failed");
  }
  async ttConflicts(schoolId: number, timetableId: number): Promise<{ conflicts: TTConflict[]; count: number }> {
    throw new Error("Supabase conflicts calculation not implemented yet (Django removed)");
  }
  async ttStatistics(schoolId: number, timetableId: number): Promise<{ total_schedules: number; teacher_stats: { name: string; total_periods: number; max_consecutive: number; days: Record<string, number[]> }[] }> {
    throw new Error("Supabase statistics calculation not implemented yet (Django removed)");
  }

  // Manual Assignment
  async ttUnassignedList(schoolId: number, timetableId: number): Promise<TTUnassignedItem[]> {
    const s = await getUnassignedListFromSupabase(schoolId, timetableId);
    if (s) return s as unknown as TTUnassignedItem[];
    throw new Error("Supabase unassigned list failed");
  }
  async ttManualAssign(schoolId: number, timetableId: number, assignmentId: number, day: string, period: number, expectedClassroomId?: number): Promise<TTSchedule> {
    const r = await manualAssignInSupabase(schoolId, timetableId, assignmentId, day, period, expectedClassroomId);
    if (r) {
      const msg = Array.isArray((r as any).errors) && (r as any).errors.length
        ? (r as any).errors.map((e: any) => e?.message || e?.code || '오류').filter(Boolean).join('\n')
        : (r as any).error;
      if (!r.success) throw { response: { data: { error: msg, errors: (r as any).errors } } };
      return { id: r.schedule_id } as unknown as TTSchedule;
    }
    throw new Error("Supabase manual assign failed");
  }
  async ttManualSwap(schoolId: number, timetableId: number, sourceId: number, targetId: number, options?: { overrideFixed?: boolean }): Promise<{ success: boolean }> {
    const r = await swapSchedulesInSupabase(schoolId, sourceId, targetId, { overrideFixed: options?.overrideFixed });
    if (r) {
      const msg = Array.isArray((r as any).errors) && (r as any).errors.length
        ? (r as any).errors.map((e: any) => e?.message || e?.code || '오류').filter(Boolean).join('\n')
        : (r as any).error;
      if (!r.success) throw { response: { data: { error: msg, errors: (r as any).errors } } };
      return { success: true };
    }
    throw new Error("Supabase manual swap failed");
  }
  async ttManualRemove(schoolId: number, timetableId: number, scheduleId: number, options?: { overrideFixed?: boolean }): Promise<{ success: boolean; message: string }> {
    const r = await removeScheduleInSupabase(schoolId, scheduleId, { overrideFixed: options?.overrideFixed });
    if (r) {
      if (!r.success) throw { response: { data: { error: r.error } } };
      return { success: true, message: '미배정 목록으로 이동되었습니다.' };
    }
    throw new Error("Supabase manual remove failed");
  }

  // ==========================================
  // Admin
  // ==========================================
  private async adminMailingFetch<T>(
    path: string,
    options: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const { supabase } = await import('./supabase');
    const token = supabase ? (await supabase.auth.getSession()).data.session?.access_token : null;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const res = await fetch(`${origin}${path}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || '메일 API 요청에 실패했습니다.');
    }
    return res.json();
  }

  async listMailingCampaigns(params?: { limit?: number; offset?: number }): Promise<{
    success: boolean;
    campaigns: MailingCampaign[];
    total: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.adminMailingFetch(`/api/admin/mailing/campaigns${suffix}`);
  }

  async listMailingGroups(): Promise<{
    success: boolean;
    groups: MailingGroup[];
  }> {
    return this.adminMailingFetch('/api/admin/mailing/groups');
  }

  async createMailingCampaign(payload: {
    groupId: number;
    subject: string;
    message: string;
    onlySupabaseLinked?: boolean;
  }): Promise<{ success: boolean; campaign: MailingCampaign }> {
    return this.adminMailingFetch('/api/admin/mailing/campaigns', {
      method: 'POST',
      body: payload,
    });
  }

  async getMailingCampaign(campaignId: string): Promise<{ success: boolean; campaign: MailingCampaign }> {
    return this.adminMailingFetch(`/api/admin/mailing/campaigns/${campaignId}`);
  }

  async startMailingCampaign(campaignId: string): Promise<{
    success: boolean;
    campaign: MailingCampaign;
    warning?: 'NO_RECIPIENTS';
  }> {
    return this.adminMailingFetch(`/api/admin/mailing/campaigns/${campaignId}/start`, { method: 'POST' });
  }

  async pauseMailingCampaign(campaignId: string): Promise<{ success: boolean; campaign: MailingCampaign }> {
    return this.adminMailingFetch(`/api/admin/mailing/campaigns/${campaignId}/pause`, { method: 'POST' });
  }

  async cancelMailingCampaign(campaignId: string): Promise<{ success: boolean; campaign: MailingCampaign }> {
    return this.adminMailingFetch(`/api/admin/mailing/campaigns/${campaignId}/cancel`, { method: 'POST' });
  }

  async retryFailedMailingCampaign(campaignId: string): Promise<{
    success: boolean;
    resetCount: number;
    campaign: MailingCampaign;
  }> {
    return this.adminMailingFetch(`/api/admin/mailing/campaigns/${campaignId}/retry-failed`, { method: 'POST' });
  }

  async resumeMailingCampaign(campaignId: string): Promise<{ success: boolean; campaign: MailingCampaign }> {
    return this.adminMailingFetch(`/api/admin/mailing/campaigns/${campaignId}/resume`, { method: 'POST' });
  }

  async listMailingFailures(
    campaignId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<{
    success: boolean;
    failures: MailingFailure[];
    total: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.adminMailingFetch(`/api/admin/mailing/campaigns/${campaignId}/failures${suffix}`);
  }

  /** @deprecated Use createMailingCampaign + startMailingCampaign */
  async adminMailing(payload: {
    groupId: number;
    subject: string;
    message: string;
    onlySupabaseLinked?: boolean;
    offset?: number;
  }): Promise<{
    success: boolean;
    total: number;
    sent: number;
    failed: number;
    hasMore: boolean;
    nextOffset: number | null;
    campaignId?: string;
    deprecated?: boolean;
    message?: string;
  }> {
    const created = await this.createMailingCampaign(payload);
    const started = await this.startMailingCampaign(created.campaign.id);
    return {
      success: true,
      total: started.campaign.totalRecipients,
      sent: started.campaign.sentCount,
      failed: started.campaign.failedCount,
      hasMore: started.campaign.status === 'running' || started.campaign.status === 'preparing',
      nextOffset: null,
      campaignId: started.campaign.id,
      deprecated: true,
      message: '백그라운드 캠페인으로 발송이 시작되었습니다.',
    };
  }

  // ──────────────────────────────────────────────────────────
  // 협업: 멤버 관리
  // ──────────────────────────────────────────────────────────
  async ttMemberList(schoolId: number): Promise<TTSchoolMember[]> {
    const raw: any = await this.workmateRequest(`/api/timetable/schools/${schoolId}/members/`);
    return raw?.members ?? [];
  }
  async ttMemberAdd(schoolId: number, userId: number, role: 'admin' | 'viewer'): Promise<TTSchoolMember[]> {
    const raw: any = await this.workmateRequest(`/api/timetable/schools/${schoolId}/members/`, {
      method: 'POST', body: JSON.stringify({ user_id: userId, role }),
    });
    return raw?.members ?? [];
  }
  async ttMemberUpdate(schoolId: number, userId: number, role: 'admin' | 'viewer'): Promise<TTSchoolMember[]> {
    const raw: any = await this.workmateRequest(`/api/timetable/schools/${schoolId}/members/`, {
      method: 'PATCH', body: JSON.stringify({ user_id: userId, role }),
    });
    return raw?.members ?? [];
  }
  async ttMemberRemove(schoolId: number, userId: number): Promise<TTSchoolMember[]> {
    const raw: any = await this.workmateRequest(`/api/timetable/schools/${schoolId}/members/`, {
      method: 'DELETE', body: JSON.stringify({ user_id: userId }),
    });
    return raw?.members ?? [];
  }

  // ──────────────────────────────────────────────────────────
  // 협업: 교사 계정 연결
  // ──────────────────────────────────────────────────────────
  async ttTeacherLink(schoolId: number, teacherId: number, userId: number, options?: { onlyIfUnlinked: boolean }): Promise<{ ok: boolean }> {
    return this.workmateRequest<{ ok: boolean }>(`/api/timetable/schools/${schoolId}/teachers/${teacherId}/link/`, {
      method: 'POST', body: JSON.stringify({ user_id: userId, only_if_unlinked: options?.onlyIfUnlinked }),
    });
  }
  async ttTeacherUnlink(schoolId: number, teacherId: number): Promise<{ ok: boolean }> {
    return this.workmateRequest<{ ok: boolean }>(`/api/timetable/schools/${schoolId}/teachers/${teacherId}/link/`, {
      method: 'DELETE',
    });
  }

  // ──────────────────────────────────────────────────────────
  // 협업: 사용자 검색
  // ──────────────────────────────────────────────────────────
  async ttUserSearch(q: string, includeSelf = false): Promise<TTUserSearchResult[]> {
    const params = new URLSearchParams({ q });
    if (includeSelf) params.set('include_self', '1');
    const raw: any = await this.workmateRequest(`/api/timetable/users/search/?${params.toString()}`);
    return raw?.results ?? [];
  }
}

export interface MailingCampaign {
  id: string;
  groupId: number;
  groupName: string | null;
  subject: string;
  message: string;
  onlySupabaseLinked: boolean;
  status: 'draft' | 'preparing' | 'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  pendingCount: number;
  progressPercent: number;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MailingGroup {
  id: number;
  name: string;
  memberCount: number;
}

export interface MailingFailure {
  id: string;
  userId: number;
  email: string;
  attemptCount: number;
  maxAttempts: number;
  lastError: string | null;
  updatedAt: string;
}

export const apiClient = new ApiClient(API_BASE_URL);

/**
 * 미디어 URL(이미지, 파일 등)을 절대 URL로 변환합니다.
 * 상대 경로(/media/...)인 경우 API 베이스 URL과 결합합니다.
 */
export function getMediaUrl(url: string | null | undefined): string {
  if (!url) return '';
  // 이미 전체 URL인 경우 (Supabase Storage 이중 슬래시 보정)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return fixSupabasePublicStorageUrl(url);
  }
  // 상대 경로인 경우 API 베이스 URL과 결합
  if (url.startsWith('/')) {
    const resolved = `${API_BASE_URL}${url}`;
    emitDjangoDependency({
      kind: "media-url",
      url: resolved,
      endpoint: "getMediaUrl",
      method: "GET",
      at: new Date().toISOString(),
    });
    return resolved;
  }
  // 그 외의 경우 그대로 반환
  return url;
}

function nonEmptyString(s: string | null | undefined): s is string {
  return typeof s === "string" && s.trim().length > 0;
}

/** DB 경로(comment_image / comment_file) 또는 기존 *_url을 화면용 절대 URL로 통일 */
function resolveCommentAttachmentUrl(raw: string | null | undefined): string | null {
  if (!nonEmptyString(raw)) return null;
  const p = raw.trim();
  if (p.startsWith("http://") || p.startsWith("https://")) return fixSupabasePublicStorageUrl(p);
  if (p.startsWith("/")) {
    const u = getMediaUrl(p);
    return u || null;
  }
  const u = getSupabaseMediaUrl(p);
  return u || null;
}

type CommunityCommentWithDbPaths = CommunityComment & {
  comment_image?: string | null;
  comment_file?: string | null;
};

function resolveCommentFileName(
  c: CommunityCommentWithDbPaths,
  resolvedFileUrl: string | null
): string | null {
  if (nonEmptyString(c.comment_file_name)) return c.comment_file_name.trim();
  if (nonEmptyString(c.comment_file)) {
    const seg = c.comment_file.trim().split("/").pop();
    return seg || null;
  }
  if (resolvedFileUrl) {
    try {
      const pathname = new URL(resolvedFileUrl).pathname;
      const seg = pathname.split("/").filter(Boolean).pop();
      return seg || null;
    } catch {
      const seg = resolvedFileUrl.split("/").pop();
      return seg || null;
    }
  }
  return null;
}

/** 저장 경로(comment_*)가 있으면 *_url보다 우선 — 잘못된/빈 URL이 와도 스토리지 경로로 복구 */
function pickCommentImageRaw(c: CommunityCommentWithDbPaths): string | null {
  if (nonEmptyString(c.comment_image)) return c.comment_image!.trim();
  if (nonEmptyString(c.comment_image_url)) return c.comment_image_url!.trim();
  return null;
}

function pickCommentFileRaw(c: CommunityCommentWithDbPaths): string | null {
  if (nonEmptyString(c.comment_file)) return c.comment_file!.trim();
  if (nonEmptyString(c.comment_file_url)) return c.comment_file_url!.trim();
  return null;
}

/** tboard_comment 스키마(comment_image / comment_file)와 UI(comment_*_url) 불일치 보정, replies 재귀 */
export function normalizeCommunityComment(c: CommunityCommentWithDbPaths): CommunityComment {
  const imgRaw = pickCommentImageRaw(c);
  const fileRaw = pickCommentFileRaw(c);

  const comment_image_url = resolveCommentAttachmentUrl(imgRaw);
  const comment_file_url = resolveCommentAttachmentUrl(fileRaw);
  const comment_file_name = resolveCommentFileName(c, comment_file_url);

  const replies = Array.isArray(c.replies) ? c.replies.map((r) => normalizeCommunityComment(r as CommunityCommentWithDbPaths)) : [];

  return {
    ...c,
    comment_image_url,
    comment_file_url,
    comment_file_name,
    num_likes: Number(c.num_likes ?? 0),
    is_liked: Boolean(c.is_liked),
    replies,
  };
}

export function normalizeCommunityCommentsList(comments: CommunityComment[]): CommunityComment[] {
  return comments.map((c) => normalizeCommunityComment(c as CommunityCommentWithDbPaths));
}

/**
 * @deprecated getMediaUrl을 사용하세요
 */
export function getImageUrl(url: string | null | undefined): string {
  return getMediaUrl(url);
}
